<template>
    <div class="ecommerce-onboarding public-ecommerce-onboarding">
        <b-container fluid class="d-flex align-items-start py-4">
            <!-- Loading State -->
            <b-row v-if="loadingSettings" class="w-100" align-h="center">
                <b-col cols="12" class="text-center">
                    <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
                    <p class="mt-3 text-muted">{{ $t('ecommerce.loading') }}</p>
                </b-col>
            </b-row>

            <!-- Onboarding Content -->
            <b-row v-else class="w-100" align-h="center">
                <b-col cols="12" xl="11">
                    <!-- Progress Indicator -->
                    <div v-if="!sessionExpiredMessage" class="progress-bar-container mb-5">
                        <div class="progress-steps">
                            <div
                                v-for="(step, index) in steps"
                                :key="index"
                                class="progress-step"
                                :class="{
                                    active: currentStep === index,
                                    completed: currentStep > index
                                }"
                            >
                                <div class="step-circle">
                                    <b-icon v-if="currentStep > index" icon="check" />
                                    <span v-else>{{ index + 1 }}</span>
                                </div>
                                <div class="step-label">{{ step.label }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Step Content -->
                    <b-card class="onboarding-card">
                        <div v-if="sessionExpiredMessage" class="onboarding-session-expired text-center py-5">
                            <div class="step-icon mb-3">
                                <b-icon icon="shield-lock" font-scale="3" variant="warning"></b-icon>
                            </div>
                            <h2 class="step-title">{{ $t('ecommerce.auth.sessionExpiredTitle') }}</h2>
                            <p class="step-description mb-4">{{ $t('ecommerce.auth.sessionExpiredDescription') }}</p>

                            <b-button variant="primary" size="lg" :to="{ name: 'Login' }">
                                <b-icon icon="box-arrow-in-right" class="mr-2"></b-icon>
                                {{ $t('ecommerce.auth.signInToContinue') }}
                            </b-button>
                        </div>

                        <template v-else>

                        <!-- Step 0: Welcome -->
                        <div v-if="currentStep === 0" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="cart-check" font-scale="3.5" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.welcome.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.welcome.description') }}</p>
                            </div>

                            <div class="features-list">
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature1') }}</span>
                                </div>
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature2') }}</span>
                                </div>
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature3') }}</span>
                                </div>
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature4') }}</span>
                                </div>
                                <div class="feature-item">
                                    <b-icon icon="check-circle-fill" variant="success"></b-icon>
                                    <span>{{ $t('ecommerce.welcome.feature5') }}</span>
                                </div>
                            </div>

                            <div v-if="integrationPrice" class="text-center mt-4">
                                <div class="pricing-box">
                                    <h5>{{ $t('ecommerce.welcome.pricingTitle') }}</h5>
                                    <div class="pricing-amount">{{ integrationPrice.formatted }}</div>
                                    <small class="text-muted">{{ $t('ecommerce.welcome.perMonth') }}</small>
                                </div>
                            </div>
                        </div>

                        <!-- Step 1: Registration -->
                        <div v-if="currentStep === 1" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="person-plus" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('registration.title') }}</h2>
                                <p class="step-description">{{ $t('registration.description') }}</p>
                            </div>

                            <b-form @submit.prevent="registerUser" class="mx-auto" style="max-width: 500px;">
                                <b-form-group :label="$t('registration.name')" label-for="reg-name">
                                    <b-form-input
                                        id="reg-name"
                                        v-model="registerForm.name"
                                        :placeholder="$t('registration.namePlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-form-group :label="$t('registration.email')" label-for="reg-email">
                                    <b-form-input
                                        id="reg-email"
                                        v-model="registerForm.email"
                                        type="email"
                                        :placeholder="$t('registration.emailPlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-form-group :label="$t('registration.projectName')" label-for="reg-project">
                                    <b-form-input
                                        id="reg-project"
                                        v-model="registerForm.projectName"
                                        :placeholder="$t('registration.projectNamePlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-form-group :label="$t('registration.password')" label-for="reg-password">
                                    <b-form-input
                                        id="reg-password"
                                        v-model="registerForm.password"
                                        type="password"
                                        :placeholder="$t('registration.passwordPlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-form-group :label="$t('registration.confirmPassword')" label-for="reg-confirm">
                                    <b-form-input
                                        id="reg-confirm"
                                        v-model="registerForm.confirmPassword"
                                        type="password"
                                        :placeholder="$t('registration.confirmPasswordPlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-alert variant="danger" :show="!!registerError" class="mt-3">{{ registerError }}</b-alert>
                            </b-form>
                        </div>

                        <!-- Step 2: Company Info -->
                        <div v-if="currentStep === 2" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="building" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.companyInfo.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.companyInfo.description') }}</p>
                            </div>

                            <b-form class="mx-auto" style="max-width: 600px;">
                                <!-- Country -->
                                <b-form-group label-for="company-country">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.country') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-select
                                        id="company-country"
                                        v-model="formData.companyInfo.country"
                                        :options="countryOptions"
                                        :disabled="!!predefinedCountry"
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- RS-only: PIB -->
                                <b-form-group v-if="isSerbian" label-for="company-pib">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.pib') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-input-group>
                                        <b-form-input
                                            id="company-pib"
                                            :value="formData.companyInfo.pib"
                                            @input="onPibInput"
                                            :placeholder="$t('ecommerce.companyInfo.pibPlaceholder')"
                                            maxlength="9"
                                            size="lg"
                                        />
                                        <b-input-group-append v-if="fetchingCompanyData">
                                            <b-input-group-text>
                                                <b-spinner small></b-spinner>
                                            </b-input-group-text>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <small class="text-muted">{{ $t('ecommerce.companyInfo.pibHint') }}</small>
                                    <b-alert variant="danger" :show="!!pibError" class="mt-2 mb-0 py-1 px-2">
                                        <small>{{ pibError }}</small>
                                    </b-alert>
                                </b-form-group>

                                <!-- RS-only: Legal Name -->
                                <b-form-group v-if="isSerbian" label-for="company-legal-name">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.legalName') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="company-legal-name"
                                        v-model="formData.companyInfo.legalName"
                                        :placeholder="$t('ecommerce.companyInfo.legalNamePlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- All: Company Name -->
                                <b-form-group label-for="company-name">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.companyName') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="company-name"
                                        v-model="formData.companyInfo.companyName"
                                        :placeholder="$t('ecommerce.companyInfo.companyNamePlaceholder')"
                                        maxlength="40"
                                        required
                                        size="lg"
                                    />
                                    <small class="text-muted">{{ $t('ecommerce.companyInfo.companyNameHint') }}</small>
                                </b-form-group>

                                <!-- RS-only: Address fields -->
                                <b-form-group v-if="isSerbian" label-for="company-address">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.companyAddress') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="company-address"
                                        v-model="formData.companyInfo.companyAddress"
                                        :placeholder="$t('ecommerce.companyInfo.companyAddressPlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <b-row v-if="isSerbian">
                                    <b-col md="8">
                                        <b-form-group label-for="company-city">
                                            <template #label>
                                                {{ $t('ecommerce.companyInfo.companyCity') }} <span class="text-danger">*</span>
                                            </template>
                                            <b-form-input
                                                id="company-city"
                                                v-model="formData.companyInfo.companyCity"
                                                :placeholder="$t('ecommerce.companyInfo.companyCityPlaceholder')"
                                                required
                                                size="lg"
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="4">
                                        <b-form-group label-for="company-postal">
                                            <template #label>
                                                {{ $t('ecommerce.companyInfo.companyPostalCode') }} <span class="text-danger">*</span>
                                            </template>
                                            <b-form-input
                                                id="company-postal"
                                                v-model="formData.companyInfo.companyPostalCode"
                                                :placeholder="$t('ecommerce.companyInfo.companyPostalCodePlaceholder')"
                                                required
                                                size="lg"
                                            />
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <!-- RS-only: Phone -->
                                <b-form-group v-if="isSerbian" label-for="company-phone">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.companyPhone') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-input-group>
                                        <b-input-group-prepend>
                                            <b-input-group-text>+381</b-input-group-text>
                                        </b-input-group-prepend>
                                        <b-form-input
                                            id="company-phone"
                                            v-model="formData.companyInfo.companyPhone"
                                            :placeholder="$t('ecommerce.companyInfo.companyPhonePlaceholder')"
                                            required
                                            size="lg"
                                        />
                                    </b-input-group>
                                </b-form-group>

                                <!-- All: Email -->
                                <b-form-group label-for="company-email">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.companyEmail') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="company-email"
                                        v-model="formData.companyInfo.companyEmail"
                                        :placeholder="$t('ecommerce.companyInfo.companyEmailPlaceholder')"
                                        type="email"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- RS-only: Legal representative -->
                                <b-form-group v-if="isSerbian" label-for="legal-representative">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.legalRepresentative') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="legal-representative"
                                        v-model="formData.companyInfo.legalRepresentative"
                                        :placeholder="$t('ecommerce.companyInfo.legalRepresentativePlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- RS-only: MB + Activity Code -->
                                <b-row v-if="isSerbian">
                                    <b-col md="6">
                                        <b-form-group :label="$t('ecommerce.companyInfo.mb')" label-for="company-mb">
                                            <b-form-input
                                                id="company-mb"
                                                v-model="formData.companyInfo.mb"
                                                :placeholder="$t('ecommerce.companyInfo.mbPlaceholder')"
                                                size="lg"
                                                readonly
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="6">
                                        <b-form-group :label="$t('ecommerce.companyInfo.activityCode')" label-for="company-activity">
                                            <b-form-input
                                                id="company-activity"
                                                v-model="formData.companyInfo.activityCode"
                                                :placeholder="$t('ecommerce.companyInfo.activityCodePlaceholder')"
                                                size="lg"
                                                readonly
                                            />
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <!-- PDV Registration - shown for all countries -->
                                <b-form-group label-for="company-pdv">
                                    <template #label>
                                        {{ $t('ecommerce.companyInfo.pdvQuestion') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-button-group size="lg" class="w-100">
                                        <b-button
                                            :variant="formData.companyInfo.pdv === true ? 'primary' : 'outline-primary'"
                                            @click="formData.companyInfo.pdv = true"
                                        >
                                            {{ $t('ecommerce.companyInfo.pdvYes') }}
                                        </b-button>
                                        <b-button
                                            :variant="formData.companyInfo.pdv === false ? 'primary' : 'outline-primary'"
                                            @click="formData.companyInfo.pdv = false"
                                        >
                                            {{ $t('ecommerce.companyInfo.pdvNo') }}
                                        </b-button>
                                    </b-button-group>
                                </b-form-group>
                            </b-form>
                        </div>

                        <!-- Step 3: Store Configuration -->
                        <div v-if="currentStep === 3" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="gear" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.store.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.store.description') }}</p>
                            </div>

                            <b-form class="mx-auto" style="max-width: 600px;">
                                <!-- Store Name -->
                                <b-form-group label-for="store-name">
                                    <template #label>
                                        {{ $t('ecommerce.store.storeName') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-input
                                        id="store-name"
                                        v-model="formData.storeName"
                                        :placeholder="$t('ecommerce.store.storeNamePlaceholder')"
                                        required
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- Store URL -->
                                <b-form-group label-for="store-url">
                                    <template #label>
                                        {{ $t('ecommerce.store.storeUrl') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-input-group>
                                        <b-form-input
                                            id="store-url"
                                            v-model="formData.storeUrl"
                                            :placeholder="$t('ecommerce.store.storeUrlPlaceholder')"
                                            required
                                            size="lg"
                                            @input="storeUrlManuallyEdited = true"
                                            @blur="validateDomain"
                                        />
                                        <b-input-group-append>
                                            <b-input-group-text>.{{ shopDomain }}</b-input-group-text>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <small v-if="!domainValidationError && !domainValidationSuccess" class="text-muted">
                                        {{ $t('ecommerce.store.storeUrlHint', { domain: fullStoreDomain }) }}
                                    </small>
                                    <small v-if="domainValidationError" class="text-danger">{{ domainValidationError }}</small>
                                    <small v-if="domainValidationSuccess" class="text-success">
                                        <b-icon icon="check-circle"></b-icon> {{ fullStoreDomain }}
                                    </small>
                                    <div v-if="validatingDomain" class="mt-1">
                                        <b-spinner small variant="primary"></b-spinner>
                                    </div>
                                </b-form-group>

                                <!-- Logo -->
                                <b-form-group label-for="store-logo">
                                    <template #label>
                                        {{ $t('ecommerce.store.logo') }}
                                    </template>
                                    <div v-if="formData.logoBase64" class="mb-2 text-center">
                                        <img :src="formData.logoBase64" alt="Logo" class="summary-logo" />
                                        <div class="mt-1">
                                            <b-button size="sm" variant="outline-danger" @click="formData.logoBase64 = ''; logoFile = null">
                                                <b-icon icon="trash"></b-icon>
                                            </b-button>
                                        </div>
                                    </div>
                                    <b-form-file
                                        v-model="logoFile"
                                        accept="image/*"
                                        :placeholder="$t('ecommerce.store.logoPlaceholder')"
                                        :drop-placeholder="$t('ecommerce.store.logoDropPlaceholder')"
                                        @change="onLogoChange"
                                    />
                                    <small class="text-muted">{{ $t('ecommerce.store.logoHint') }}</small>
                                    <b-alert variant="danger" :show="logoSizeError" class="mt-2 mb-0 py-1 px-2">
                                        <small>{{ $t('ecommerce.store.logoSizeError') }}</small>
                                    </b-alert>
                                </b-form-group>

                                <!-- Currency -->
                                <b-form-group label-for="store-currency">
                                    <template #label>
                                        {{ $t('ecommerce.store.currency') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-select
                                        id="store-currency"
                                        v-model="formData.currency"
                                        :options="currencyOptions"
                                        :disabled="isSerbian"
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- Language -->
                                <b-form-group label-for="store-language">
                                    <template #label>
                                        {{ $t('ecommerce.store.language') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-select
                                        id="store-language"
                                        v-model="formData.language"
                                        :options="languageOptions"
                                        size="lg"
                                    />
                                </b-form-group>

                                <!-- Merchant Description -->
                                <b-form-group label-for="merchant-description">
                                    <template #label>
                                        {{ $t('ecommerce.store.merchantDescription') }} <span class="text-danger">*</span>
                                    </template>
                                    <b-form-select
                                        id="merchant-description"
                                        v-model="formData.merchantDescription"
                                        :options="merchantDescriptionOptionsFormatted"
                                        size="lg"
                                    >
                                        <template #first>
                                            <b-form-select-option :value="''" disabled>{{ $t('ecommerce.store.merchantDescription') }}...</b-form-select-option>
                                        </template>
                                    </b-form-select>
                                </b-form-group>
                            </b-form>
                        </div>

                        <!-- Step 4: Product Type & Shipping Methods -->
                        <div v-if="currentStep === 4" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="box-seam" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.shipping.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.shipping.description') }}</p>
                            </div>

                            <b-form-group class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.shipping.productType') }} <span class="text-danger">*</span>
                                </template>
                                <div class="payment-type-selection">
                                    <div
                                        v-for="type in productTypes"
                                        :key="type.value"
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.productType === type.value }"
                                        @click="formData.productType = type.value"
                                    >
                                        <b-icon :icon="type.icon" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">{{ $t(type.textKey) }}</div>
                                    </div>
                                </div>
                            </b-form-group>

                            <b-form-group v-if="formData.productType === 'physical'" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.shipping.methods') }} <span class="text-danger">*</span>
                                </template>
                                <div class="payment-type-selection">
                                    <div
                                        v-for="method in shippingMethods"
                                        :key="method.value"
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.shippingMethods.includes(method.value) }"
                                        @click="toggleShippingMethod(method.value)"
                                    >
                                        <b-icon :icon="method.icon" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">{{ $t(method.textKey) }}</div>
                                    </div>
                                </div>
                            </b-form-group>
                        </div>

                        <!-- Step 5: Payment Methods -->
                        <div v-if="currentStep === 5" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="credit-card" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.payment.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.payment.description') }}</p>
                            </div>

                            <!-- Payment Type Selection -->
                            <b-form-group class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.choosePaymentType') }} <span class="text-danger">*</span>
                                </template>
                                <div class="payment-type-selection">
                                    <div
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.paymentMethods.includes('credit_card'), 'disabled': isMonriPartner }"
                                        @click="!isMonriPartner && togglePaymentMethod('credit_card')"
                                    >
                                        <b-icon icon="credit-card" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">
                                            {{ $t('ecommerce.payment.paymentCard') }}
                                            <b-badge v-if="isMonriPartner" variant="primary" class="ml-1">
                                                {{ $t('ecommerce.payment.required') }}
                                            </b-badge>

                                            <div class="payment-card-logos">
                                                <span class="payment-logo-pill">
                                                    <img :src="masterCardlogo" alt="Mastercard" class="payment-card-logo" />
                                                </span>
                                                <span class="payment-logo-pill">
                                                    <img :src="visaLogo" alt="Visa" class="payment-card-logo" />
                                                </span>
                                                <span v-if="isSerbian" class="payment-logo-pill">
                                                    <img :src="ipsLogo" alt="IPS" class="payment-card-logo" />
                                                </span>
                                                <span class="payment-logo-pill payment-logo-pill--compact">
                                                    <img :src="applePayLogo" alt="Apple Pay" class="payment-card-logo" />
                                                </span>
                                                <span class="payment-logo-pill">
                                                    <img :src="googlePayLogo" alt="Google Pay" class="payment-card-logo" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.paymentMethods.includes('bank_transfer') }"
                                        @click="togglePaymentMethod('bank_transfer')"
                                    >
                                        <b-icon icon="bank" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">{{ $t('ecommerce.payment.bankTransfer') }}</div>
                                    </div>
                                    <div
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.paymentMethods.includes('cash_on_delivery') }"
                                        @click="togglePaymentMethod('cash_on_delivery')"
                                    >
                                        <b-icon icon="cash" font-scale="2" class="mb-2"></b-icon>
                                        <div class="payment-type-name">{{ $t('ecommerce.payment.cashOnDelivery') }}</div>
                                    </div>
                                </div>
                            </b-form-group>

                            <!-- Payment Provider Selection -->
                            <b-form-group v-if="formData.paymentMethods.includes('credit_card')" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.chooseProvider') }} <span class="text-danger">*</span>
                                </template>
                                <div class="payment-type-selection">
                                    <!-- Non-Serbian Monri: show Monri logo locked -->
                                    <div
                                        v-if="!(isMonriPartner && isSerbian)"
                                        class="payment-type-box"
                                        :class="{ 'selected': formData.creditCardProvider === 'monri', 'disabled': isMonriPartner }"
                                        @click="!isMonriPartner && (formData.creditCardProvider = 'monri')"
                                    >
                                        <img :src="monriLogoBase64" alt="Monri" class="provider-logo" />
                                        <b-badge v-if="isMonriPartner" variant="primary" class="mt-2">{{ $t('ecommerce.payment.required') }}</b-badge>
                                    </div>
                                    <!-- Serbian Monri: show Chipcard logo locked -->
                                    <div
                                        v-if="isMonriPartner && isSerbian"
                                        class="payment-type-box selected disabled"
                                    >
                                        <img :src="chipcardLogoBase64" alt="Chipcard" class="provider-logo provider-logo--wide" />
                                        <b-badge variant="primary" class="mt-2">{{ $t('ecommerce.payment.required') }}</b-badge>
                                    </div>
                                </div>
                            </b-form-group>

                            <!-- Monri Credentials -->
                            <b-form-group v-if="formData.paymentMethods.includes('credit_card') && formData.creditCardProvider === 'monri' && !isMonriPartner" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.monriCredentials') }}
                                </template>
                                <b-form-checkbox v-model="formData.monriSetupLater" class="mb-3">
                                    {{ $t('ecommerce.payment.setupLater') }}
                                </b-form-checkbox>
                                <template v-if="!formData.monriSetupLater">
                                    <b-form-group :label="$t('ecommerce.payment.merchantKey')" label-for="monri-merchant-key" class="mb-3">
                                        <b-form-input
                                            id="monri-merchant-key"
                                            v-model="formData.monriMerchantKey"
                                            :placeholder="$t('ecommerce.payment.merchantKeyPlaceholder')"
                                        />
                                    </b-form-group>
                                    <b-form-group :label="$t('ecommerce.payment.authenticityToken')" label-for="monri-auth-token">
                                        <b-form-input
                                            id="monri-auth-token"
                                            v-model="formData.monriAuthenticityToken"
                                            :placeholder="$t('ecommerce.payment.authenticityTokenPlaceholder')"
                                            type="password"
                                        />
                                    </b-form-group>
                                </template>
                                <small v-if="isMonriPartner" class="text-muted">{{ $t('ecommerce.payment.monriPartnerCredentialsInfo') }}</small>
                            </b-form-group>

                            <!-- Bank Transfer Instructions Upload -->
                            <b-form-group v-if="formData.paymentMethods.includes('bank_transfer')" class="mb-4">
                                <template #label>
                                    {{ $t('ecommerce.payment.bankTransferInstruction') }}
                                </template>
                                <div v-if="!formData.bankTransferInstructionPdf" class="upload-box dashed-box" @click="$refs.bankInstructionInput.$el.querySelector('input').click()">
                                    <b-icon icon="file-earmark-text" font-scale="2" class="mb-2 text-muted"></b-icon>
                                    <p class="mb-1 text-primary">{{ $t('ecommerce.payment.bankTransferInstructionUploadPrompt') }}</p>
                                    <small class="text-muted">{{ $t('ecommerce.payment.bankTransferInstructionHint') }}</small>
                                </div>
                                <div v-else>
                                    <b-alert show variant="success" class="mb-0 py-2 px-3 d-flex align-items-center">
                                        <b-icon icon="file-earmark-check-fill" class="mr-2"></b-icon>
                                        <span>{{ $t('ecommerce.payment.bankTransferInstructionUploaded') }}</span>
                                        <b-button size="sm" variant="outline-danger" class="ml-auto" @click="formData.bankTransferInstructionPdf = ''; bankInstructionFile = null">
                                            <b-icon icon="trash"></b-icon>
                                        </b-button>
                                    </b-alert>
                                </div>
                                <b-form-file
                                    ref="bankInstructionInput"
                                    v-model="bankInstructionFile"
                                    accept=".pdf"
                                    class="d-none"
                                    @change="onBankInstructionChange"
                                />
                            </b-form-group>
                        </div>

                        <!-- Step 6: Documentation -->
                        <div v-if="currentStep === 6" class="onboarding-step">
                            <div class="text-center mb-4">
                                <div class="step-icon mb-3">
                                    <b-icon icon="file-text" font-scale="3" variant="primary"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.documentation.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.documentation.description') }}</p>
                            </div>

                            <b-alert show variant="success" class="mb-4">
                                <b-icon icon="check-circle" class="mr-2"></b-icon>
                                {{ $t('ecommerce.documentation.autoGeneratedInfo') }}
                            </b-alert>

                            <!-- TOS Preview -->
                            <div class="tos-preview-container mb-4">
                                <b-alert show variant="info" class="mb-3">
                                    <b-icon icon="info-circle" class="mr-2"></b-icon>
                                    {{ $t('ecommerce.documentation.tosPreviewInfo') }}
                                </b-alert>
                                <div v-if="loadingTosPreview" class="text-center py-4">
                                    <b-spinner variant="primary"></b-spinner>
                                </div>
                                <div v-else-if="tosPreviewHtml" class="tos-preview-frame" v-html="tosPreviewHtml"></div>
                                <div v-else class="text-center py-4 text-muted">
                                    <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                                    {{ $t('ecommerce.documentation.tosPreviewError') }}
                                </div>
                            </div>

                            <div class="document-preview mb-3">
                                <h5>{{ $t('ecommerce.documentation.termsOfService') }}</h5>
                                <p class="text-muted small">{{ $t('ecommerce.documentation.uploadCustomTosNote') }}</p>
                                <div v-if="!formData.termsOfService" class="upload-box dashed-box" @click="$refs.termsFileInput.$el.querySelector('input').click()">
                                    <b-icon icon="cloud-upload" font-scale="1.5" class="mb-2 text-primary"></b-icon>
                                    <p class="mb-1 text-primary">{{ $t('ecommerce.documentation.uploadPrompt') }}</p>
                                    <small class="text-muted">{{ $t('ecommerce.documentation.uploadHint') }}</small>
                                </div>
                                <div v-else class="preview-box">
                                    <b-alert show variant="success" class="mb-0 py-2 px-3 d-flex align-items-center">
                                        <b-icon icon="file-earmark-check-fill" class="mr-2"></b-icon>
                                        <span>{{ $t('ecommerce.documentation.customFileUploaded') }}</span>
                                        <b-button size="sm" variant="outline-danger" class="ml-auto" @click="formData.termsOfService = ''; termsOfServiceFile = null; formData.useAutoGeneratedDocs = true">
                                            <b-icon icon="trash"></b-icon>
                                        </b-button>
                                    </b-alert>
                                </div>
                                <b-form-file
                                    ref="termsFileInput"
                                    v-model="termsOfServiceFile"
                                    accept=".pdf"
                                    class="d-none"
                                    @change="onTermsOfServiceChange"
                                ></b-form-file>
                            </div>
                        </div>

                        <!-- Step 7: Summary / Preview -->
                        <div v-if="currentStep === 7" class="onboarding-step">
                            <div class="text-center mb-5">
                                <div class="preview-success-icon mb-3">
                                    <b-icon icon="check-circle-fill" font-scale="4" variant="success"></b-icon>
                                </div>
                                <h2 class="step-title">{{ $t('ecommerce.summary.title') }}</h2>
                                <p class="step-description">{{ $t('ecommerce.summary.description') }}</p>
                            </div>

                            <!-- Store Hero Banner -->
                            <div class="preview-store-banner mb-4">
                                <div class="preview-store-banner-logo">
                                    <img v-if="formData.logoBase64" :src="formData.logoBase64" alt="Store Logo" class="preview-banner-logo-img" />
                                    <div v-else class="preview-banner-logo-placeholder">
                                        <b-icon icon="shop" font-scale="2.5" variant="secondary"></b-icon>
                                    </div>
                                </div>
                                <div class="preview-store-banner-info">
                                    <div class="preview-store-name">{{ formData.storeName || formData.companyInfo.companyName || formData.storeUrl }}</div>
                                    <a :href="fullStoreDomain" target="_blank" rel="noopener" class="preview-store-url-link">
                                        <b-icon icon="globe" class="mr-1"></b-icon>{{ fullStoreDomain }}
                                    </a>
                                    <div class="preview-store-badges mt-2">
                                        <b-badge variant="primary" class="mr-1">{{ formData.currency }}</b-badge>
                                        <b-badge variant="secondary" class="mr-1">{{ getLanguageText(formData.language) }}</b-badge>
                                        <b-badge v-if="formData.productType" variant="light" class="mr-1">
                                            <b-icon :icon="getProductTypeIcon(formData.productType)" class="mr-1"></b-icon>
                                            {{ getProductTypeLabel(formData.productType) }}
                                        </b-badge>
                                    </div>
                                </div>
                            </div>

                            <!-- Preview Cards Grid -->
                            <div class="preview-cards-grid">
                                <!-- Company Info Card -->
                                <div class="preview-card">
                                    <div class="preview-card-header">
                                        <div class="preview-card-header-icon">
                                            <b-icon icon="building" variant="primary"></b-icon>
                                        </div>
                                        <h6>{{ $t('ecommerce.summary.companyInfo') }}</h6>
                                    </div>
                                    <div class="preview-card-body">
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.country') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.country }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.pib') }}</span>
                                            <span class="preview-value preview-mono">{{ formData.companyInfo.pib }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.legalName') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.legalName }}</span>
                                        </div>
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyName') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.companyName }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyAddress') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.companyAddress }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyCity') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.companyCity }}<span v-if="formData.companyInfo.companyPostalCode">, {{ formData.companyInfo.companyPostalCode }}</span></span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyPhone') }}</span>
                                            <span class="preview-value preview-mono">+381{{ formData.companyInfo.companyPhone }}</span>
                                        </div>
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.companyEmail') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.companyEmail }}</span>
                                        </div>
                                        <div v-if="isSerbian" class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.legalRepresentative') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.legalRepresentative }}</span>
                                        </div>
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.companyInfo.pdvQuestion') }}</span>
                                            <span class="preview-value">{{ formData.companyInfo.pdv === true ? $t('ecommerce.companyInfo.pdvYes') : $t('ecommerce.companyInfo.pdvNo') }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Payment Methods Card -->
                                <div class="preview-card">
                                    <div class="preview-card-header">
                                        <div class="preview-card-header-icon">
                                            <b-icon icon="credit-card" variant="primary"></b-icon>
                                        </div>
                                        <h6>{{ $t('ecommerce.summary.paymentInfo') }}</h6>
                                    </div>
                                    <div class="preview-card-body">
                                        <div v-for="method in formData.paymentMethods" :key="method" class="preview-method-item">
                                            <div class="preview-method-icon">
                                                <b-icon :icon="getPaymentMethodIcon(method)" variant="primary"></b-icon>
                                            </div>
                                            <div class="preview-method-content">
                                                <span class="preview-method-name">{{ getPaymentMethodLabel(method) }}</span>
                                            </div>
                                        </div>
                                        <div v-if="formData.paymentMethods.length === 0" class="text-muted small py-2">—</div>
                                    </div>
                                </div>

                                <!-- Shipping / Category Card -->
                                <div class="preview-card">
                                    <div class="preview-card-header">
                                        <div class="preview-card-header-icon">
                                            <b-icon :icon="formData.productType === 'physical' ? 'truck' : (formData.productType === 'digital' ? 'file-earmark-code' : 'tools')" variant="primary"></b-icon>
                                        </div>
                                        <h6>{{ $t('ecommerce.summary.shippingInfo') }}</h6>
                                    </div>
                                    <div class="preview-card-body">
                                        <div v-if="formData.productType" class="preview-row mb-2">
                                            <span class="preview-label">{{ $t('ecommerce.shipping.productType') }}</span>
                                            <b-badge variant="primary">{{ getProductTypeLabel(formData.productType) }}</b-badge>
                                        </div>
                                        <div v-if="formData.productType === 'physical' && formData.shippingMethods.length > 0">
                                            <div v-for="method in formData.shippingMethods" :key="method" class="preview-method-item">
                                                <div class="preview-method-icon">
                                                    <b-icon :icon="getShippingMethodIcon(method)" variant="primary"></b-icon>
                                                </div>
                                                <div class="preview-method-content">
                                                    <span class="preview-method-name">{{ getShippingMethodLabel(method) }}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="formData.productType !== 'physical'" class="text-muted small py-1">
                                            {{ $t('ecommerce.summary.noShippingRequired') }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Legal Documents Card -->
                                <div class="preview-card">
                                    <div class="preview-card-header">
                                        <div class="preview-card-header-icon">
                                            <b-icon icon="file-text" variant="primary"></b-icon>
                                        </div>
                                        <h6>{{ $t('ecommerce.steps.documentation') }}</h6>
                                    </div>
                                    <div class="preview-card-body">
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.documentation.termsOfService') }}</span>
                                            <b-badge :variant="formData.termsOfService ? 'success' : 'info'" class="preview-doc-badge">
                                                <b-icon :icon="formData.termsOfService ? 'check-circle-fill' : 'magic'" class="mr-1"></b-icon>
                                                {{ formData.termsOfService ? $t('ecommerce.documentation.customFileUploaded') : $t('ecommerce.summary.autoGenerated') }}
                                            </b-badge>
                                        </div>
                                        <div class="preview-row">
                                            <span class="preview-label">{{ $t('ecommerce.store.merchantDescription') }}</span>
                                            <span class="preview-value preview-value--sm">{{ getMerchantDescriptionText() }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Custom Domains Section -->
                            <div class="preview-card preview-card--full mt-4">
                                <div class="preview-card-header">
                                    <div class="preview-card-header-icon">
                                        <b-icon icon="globe" variant="primary"></b-icon>
                                    </div>
                                    <h6>{{ $t('ecommerce.summary.domainsInfo') }}</h6>
                                </div>
                                <div class="preview-card-body">
                                    <div v-if="!showCustomDomainSetup && formData.customDomains.length === 0">
                                        <p class="text-muted small mb-2">{{ $t('ecommerce.domains.description') }}</p>
                                        <b-button variant="outline-primary" size="sm" @click="showCustomDomainSetup = true">
                                            <b-icon icon="plus-circle" class="mr-1"></b-icon>{{ $t('ecommerce.domains.setupCustomDomain') }}
                                        </b-button>
                                    </div>
                                    <div v-else>
                                        <p class="text-muted small mb-3">{{ $t('ecommerce.domains.description') }}</p>
                                        <b-form @submit.prevent="addCustomDomain">
                                            <b-input-group>
                                                <b-form-input v-model="newDomain" :placeholder="$t('ecommerce.domains.placeholder')" size="lg" />
                                                <b-input-group-append>
                                                    <b-button variant="primary" type="submit" :disabled="!newDomain">
                                                        <b-icon icon="plus"></b-icon> {{ $t('ecommerce.domains.add') }}
                                                    </b-button>
                                                </b-input-group-append>
                                            </b-input-group>
                                        </b-form>
                                        <div v-if="formData.customDomains.length > 0" class="mt-3">
                                            <b-list-group>
                                                <b-list-group-item
                                                    v-for="(domain, index) in formData.customDomains"
                                                    :key="index"
                                                    class="d-flex justify-content-between align-items-center"
                                                >
                                                    <span><b-icon icon="globe" class="mr-2 text-primary"></b-icon>{{ domain }}</span>
                                                    <b-button variant="outline-danger" size="sm" @click="removeDomain(index)">
                                                        <b-icon icon="trash"></b-icon>
                                                    </b-button>
                                                </b-list-group-item>
                                            </b-list-group>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Step 8: Complete -->
                        <div v-if="currentStep === 8" class="onboarding-step">
                            <div class="text-center py-5">
                                <div class="public-onboarding__complete-icon mx-auto mb-4">✓</div>
                                <h2 class="step-title">{{ $t('complete.title') }}</h2>
                                <p class="step-description mb-4">{{ $t('complete.description') }}</p>
                                <p class="text-muted">{{ $t('complete.checkEmail') }}</p>

                                <!-- Chipcard preparation info for Monri partners -->
                                <div v-if="isMonriPartner" class="mt-5 text-left mx-auto" style="max-width: 650px;">
                                    <b-alert show variant="warning" class="mb-4">
                                        <div class="d-flex align-items-start">
                                            <b-icon icon="info-circle-fill" class="mr-3 mt-1" font-scale="1.3"></b-icon>
                                            <div>
                                                <h5 class="mb-2">{{ $t('complete.chipcardTitle') }}</h5>
                                                <p class="mb-3">{{ $t('complete.chipcardDescription') }}</p>
                                                <ul class="mb-0 pl-3">
                                                    <li class="mb-2">{{ $t('complete.chipcardDoc1') }}</li>
                                                    <li class="mb-2">{{ $t('complete.chipcardDoc2') }}</li>
                                                    <li class="mb-2">{{ $t('complete.chipcardDoc3') }}</li>
                                                    <li class="mb-0">{{ $t('complete.chipcardDoc4') }}</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </b-alert>
                                </div>

                                <b-button
                                    variant="primary"
                                    size="lg"
                                    class="mt-4"
                                    :to="{ name: 'Login' }"
                                >
                                    <b-icon icon="box-arrow-in-right" class="mr-2"></b-icon>
                                    {{ $t('complete.backToLogin') }}
                                </b-button>
                            </div>
                        </div>

                        <!-- Navigation Buttons -->
                        <div v-if="currentStep < 8" class="navigation-buttons mt-4">
                            <b-button
                                v-if="currentStep > 0 && currentStep !== 8"
                                variant="outline-secondary"
                                @click="prevStep"
                                :disabled="false"
                                size="lg"
                            >
                                <b-icon icon="arrow-left"></b-icon> {{ $t('back') }}
                            </b-button>

                            <b-button
                                v-if="currentStep === 1"
                                variant="primary"
                                @click="registerUser"
                                :disabled="!canProceed || registering"
                                size="lg"
                                class="ml-auto"
                            >
                                <b-spinner small v-if="registering" class="mr-1"></b-spinner>
                                {{ $t('registration.createAccount') }} <b-icon icon="arrow-right"></b-icon>
                            </b-button>

                            <b-button
                                v-else-if="currentStep < 7"
                                variant="primary"
                                @click="nextStep"
                                :disabled="!canProceed"
                                size="lg"
                                class="ml-auto"
                            >
                                {{ $t('continue') }} <b-icon icon="arrow-right"></b-icon>
                            </b-button>

                            <b-button
                                v-if="currentStep === 7"
                                variant="success"
                                @click="completeOnboarding"
                                :disabled="loading"
                                size="lg"
                                class="ml-auto"
                            >
                                <b-spinner small v-if="loading"></b-spinner>
                                {{ loading ? $t('saving') : $t('finish') }}
                            </b-button>
                        </div>
                        </template>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <!-- Support Footer -->
        <div class="onboarding-support-footer">
            <a href="mailto:support@protokol.io" class="onboarding-support-link">
                <b-icon icon="question-circle" class="mr-1"></b-icon>
                {{ $t('support.stuck') }}
            </a>
        </div>

        <!-- Legal Confirmation Modal -->
        <main-dialog
            v-model="showLegalModal"
            :title="$t('ecommerce.welcome.confirmationTitle')"
            @ok="handleLegalConfirmation"
            :ok-title="$t('ecommerce.welcome.confirmButton')"
            :cancel-title="$t('cancel')"
            size="lg"
            centered
            no-close-on-backdrop
            no-close-on-esc
        >
            <b-alert show variant="warning" class="mb-0">
                <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
                {{ $t('ecommerce.welcome.confirmationText') }}
            </b-alert>
        </main-dialog>
    </div>
</template>

<script>
import Axios from 'axios'
import Env from '@/plugins/protokol/lib/Env'

var CACHE_KEY = 'public_ecommerce_onboarding_cache'

export default {
    name: 'PublicEcommerceOnboarding',

    data: function() {
        return {
            currentStep: 0,
            loading: false,
            loadingSettings: false,
            loadingPrice: false,
            integrationPrice: null,
            validatingDomain: false,
            domainValidationError: null,
            domainValidationSuccess: false,
            fetchingCompanyData: false,
            pibError: null,
            showCustomDomainSetup: false,
            showLegalModal: false,
            newDomain: '',
            storeUrlManuallyEdited: false,
            validateDomainTimer: null,
            onboardingToken: null,
            integration: null,
            paymentsIntegration: null,
            ecommSettings: null,
            registering: false,
            registerError: null,
            sessionExpiredMessage: null,

            registerForm: {
                name: '',
                email: '',
                projectName: '',
                password: '',
                confirmPassword: ''
            },

            formData: {
                storeName: '',
                storeUrl: '',
                currency: 'USD',
                language: 'en',
                merchantDescription: '',
                productType: '',
                logoBase64: '',
                customDomains: [],
                creditCardProvider: null,
                monriMerchantKey: '',
                monriAuthenticityToken: '',
                monriSetupLater: false,
                creditCardReceivingMethod: null,
                paymentMethods: [],
                shippingMethods: [],
                bankTransferInstructionPdf: '',
                useAutoGeneratedDocs: true,
                termsOfService: '',
                companyInfo: {
                    country: '',
                    pib: '',
                    legalName: '',
                    companyName: '',
                    companyAddress: '',
                    companyCity: '',
                    companyPostalCode: '',
                    companyPhone: '',
                    companyEmail: '',
                    legalRepresentative: '',
                    activityCode: '',
                    mb: '',
                    pdv: null,
                    bankAccounts: [],
                    selectedBankAccount: null
                }
            },

            logoFile: null,
            logoSizeError: false,
            bankInstructionFile: null,
            termsOfServiceFile: null,
            tosPreviewHtml: '',
            loadingTosPreview: false,
            monriLogoBase64: require('./monri-logo.png'),
            chipcardLogoBase64: require('./chipcard-logo.png'),
            visaLogo: require("./visa.png"),
            masterCardlogo: require("./mastercard.png"),
            ipsLogo: require("./ips.png"),
            applePayLogo: require("./apay.png"),
            googlePayLogo: require("./gpay.png"),

            countryOptions: [
                { value: '', text: 'Select country...', disabled: true },
                { value: 'RS', text: 'Serbia' },
                { value: 'AT', text: 'Austria' },
                { value: 'BA', text: 'Bosnia and Herzegovina' },
                { value: 'HR', text: 'Croatia' },
                { value: 'CZ', text: 'Czech Republic' },
                { value: 'DE', text: 'Germany' },
                { value: 'GR', text: 'Greece' },
                { value: 'HU', text: 'Hungary' },
                { value: 'IT', text: 'Italy' },
                { value: 'ME', text: 'Montenegro' },
                { value: 'MK', text: 'North Macedonia' },
                { value: 'PL', text: 'Poland' },
                { value: 'RO', text: 'Romania' },
                { value: 'SI', text: 'Slovenia' },
                { value: 'SK', text: 'Slovakia' },
                { value: 'FR', text: 'France' },
                { value: 'ES', text: 'Spain' },
                { value: 'NL', text: 'Netherlands' },
                { value: 'BE', text: 'Belgium' },
                { value: 'SE', text: 'Sweden' },
                { value: 'NO', text: 'Norway' },
                { value: 'DK', text: 'Denmark' },
                { value: 'FI', text: 'Finland' },
                { value: 'PT', text: 'Portugal' },
                { value: 'CH', text: 'Switzerland' },
                { value: 'GB', text: 'United Kingdom' },
                { value: 'US', text: 'United States' },
                { value: 'OTHER', text: 'Other' }
            ],

            currencyOptions: [
                { value: 'USD', text: 'USD - US Dollar' },
                { value: 'EUR', text: 'EUR - Euro' },
                { value: 'RSD', text: 'RSD - Serbian Dinar' },
                { value: 'GBP', text: 'GBP - British Pound' },
                { value: 'CHF', text: 'CHF - Swiss Franc' }
            ],

            languageOptions: [
                { value: 'en', text: 'English' },
                { value: 'sr', text: 'Serbian / Srpski' }
            ],

            merchantDescriptionOptions: [
                { value: 'online_store', text: 'Online Store', text_sr: 'Online prodavnica', mcc: ['5399', '5969'] },
                { value: 'clothing_apparel', text: 'Clothing & Apparel', text_sr: 'Odeća i garderoba', mcc: ['5651'] },
                { value: 'electronics_store', text: 'Electronics Store', text_sr: 'Prodavnica elektronike', mcc: ['5732'] },
                { value: 'home_lifestyle', text: 'Home & Lifestyle Products', text_sr: 'Proizvodi za dom i stil života', mcc: ['5712'] },
                { value: 'beauty_cosmetics', text: 'Beauty & Cosmetics', text_sr: 'Lepota i kozmetika', mcc: ['5977'] },
                { value: 'sporting_goods', text: 'Sporting Goods', text_sr: 'Sportska oprema', mcc: ['5941'] },
                { value: 'digital_products', text: 'Digital Products & Downloads', text_sr: 'Digitalni proizvodi i preuzimanja', mcc: ['5815'] },
                { value: 'software_sales', text: 'Software Sales', text_sr: 'Prodaja softvera', mcc: ['5734'] },
                { value: 'online_education', text: 'Online Education Platform', text_sr: 'Online obrazovna platforma', mcc: ['8299'] },
                { value: 'professional_services', text: 'Professional Services', text_sr: 'Profesionalne usluge', mcc: ['7399'] },
                { value: 'food_beverage_delivery', text: 'Food & Beverage Delivery', text_sr: 'Dostava hrane i pića', mcc: ['5812'] }
            ],

            productTypes: [
                { value: 'physical', textKey: 'ecommerce.shipping.physical', icon: 'box-seam' },
                { value: 'digital', textKey: 'ecommerce.shipping.digital', icon: 'file-earmark-code' },
                { value: 'service', textKey: 'ecommerce.shipping.services', icon: 'tools' }
            ],

            shippingMethods: [
                { value: 'delivery', textKey: 'ecommerce.shipping.delivery', icon: 'truck' },
                { value: 'pickup', textKey: 'ecommerce.shipping.pickupFromShop', icon: 'shop' }
            ]
        }
    },

    computed: {
        shopDomain: function() {
            return Env.get("ECOMMERCE_SHOP_DOMAIN") || 'ptkl.shop'
        },

        fullStoreDomain: function() {
            var baseDomain = this.formData.storeUrl + '.' + this.shopDomain
            return 'https://' + baseDomain
        },

        merchantDescriptionOptionsFormatted: function() {
            var self = this
            return this.merchantDescriptionOptions.map(function(option) {
                return {
                    value: option.value,
                    text: self.formData.language === 'sr' ? option.text_sr : option.text
                }
            })
        },

        predefinedCountry: function() {
            return this.integration && this.integration.data && this.integration.data.country || null
        },

        isSerbian: function() {
            var integrationCountry = this.integration && this.integration.data && this.integration.data.country
            return integrationCountry === 'RS' || this.formData.companyInfo.country === 'RS'
        },

        isMonriPartner: function() {
            return !!(this.paymentsIntegration && this.paymentsIntegration.data && this.paymentsIntegration.data.partner === 'monri')
        },

        isChipcardPartner: function() {
            return !!(this.paymentsIntegration && this.paymentsIntegration.data && this.paymentsIntegration.data.partner === 'chipcard')
        },

        steps: function() {
            return [
                { label: this.$t('ecommerce.steps.welcome') },
                { label: this.$t('registration.stepLabel') },
                { label: this.$t('ecommerce.steps.companyInfo') },
                { label: this.$t('ecommerce.steps.store') },
                { label: this.$t('ecommerce.steps.shipping') },
                { label: this.$t('ecommerce.steps.payment') },
                { label: this.$t('ecommerce.steps.documentation') },
                { label: this.$t('ecommerce.steps.summary') }
            ]
        },

        canProceed: function() {
            switch (this.currentStep) {
                case 0:
                    return true
                case 1:
                    return this.registerForm.name.length >= 1 &&
                        this.registerForm.email.length >= 3 &&
                        this.registerForm.projectName.length >= 2 &&
                        this.registerForm.password.length >= 7 &&
                        this.registerForm.password === this.registerForm.confirmPassword
                case 2:
                    if (this.isSerbian) {
                        return this.formData.companyInfo.country &&
                            this.formData.companyInfo.pib.length === 9 &&
                            this.formData.companyInfo.legalName &&
                            this.formData.companyInfo.companyName &&
                            this.formData.companyInfo.companyAddress &&
                            this.formData.companyInfo.companyCity &&
                            this.formData.companyInfo.companyPostalCode &&
                            this.formData.companyInfo.companyPhone &&
                            this.formData.companyInfo.companyEmail &&
                            this.formData.companyInfo.legalRepresentative &&
                            this.formData.companyInfo.pdv !== null
                    }
                    return this.formData.companyInfo.country &&
                        this.formData.companyInfo.companyName &&
                        this.formData.companyInfo.companyEmail &&
                        this.formData.companyInfo.pdv !== null
                case 3:
                    return this.formData.storeName &&
                        this.formData.storeUrl &&
                        this.formData.currency &&
                        this.formData.language &&
                        this.formData.merchantDescription &&
                        !this.domainValidationError
                case 4:
                    if (this.formData.productType === 'physical') {
                        return this.formData.productType && this.formData.shippingMethods.length > 0
                    }
                    return !!this.formData.productType
                case 5:
                    if (this.formData.paymentMethods.length === 0) return false
                    if (this.formData.paymentMethods.includes('credit_card')) {
                        if (!this.formData.creditCardProvider) return false
                        if (this.formData.creditCardProvider === 'monri' && !this.isMonriPartner && !this.formData.monriSetupLater) {
                            if (!this.formData.monriMerchantKey || !this.formData.monriAuthenticityToken) return false
                        }
                        if (!this.formData.creditCardReceivingMethod && this.isChipcardPartner) return false
                    }
                    return true
                case 6:
                    return true
                case 7:
                    return true
                default:
                    return false
            }
        }
    },

    watch: {
        'formData.storeName': function(val) {
            if (!this.storeUrlManuallyEdited && val) {
                this.formData.storeUrl = val.toLowerCase()
                    .replace(/[^a-z0-9-]/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-|-$/g, '')
                this.validateDomainDebounced()
            }
        },

        'formData.storeUrl': function() {
            if (this.storeUrlManuallyEdited) {
                this.validateDomainDebounced()
            }
        },

        'formData.companyInfo.country': function(val) {
            if (val === 'RS') {
                this.formData.currency = 'RSD'
            }
        },

        currentStep: function(step) {
            this.saveToCache()
            if (step === 5 && this.isMonriPartner) {
                if (!this.formData.paymentMethods.includes('credit_card')) {
                    this.formData.paymentMethods.push('credit_card')
                }
                this.formData.creditCardProvider = 'monri'
            }
        }
    },

    methods: {
        createHttpClient: function(config) {
            var self = this
            var client = Axios.create(config)
            client.interceptors.response.use(function(response) {
                return response
            }, function(error) {
                self.handleUnauthorized(error)
                return Promise.reject(error)
            })
            return client
        },

        handleUnauthorized: function(error) {
            if (error && error.response && error.response.status === 401) {
                this.sessionExpiredMessage = '1'
                this.clearCache()
                this.onboardingToken = null

                return true
            }

            return false
        },

        getApiClient: function() {
            if (!this.onboardingToken) return null
            return this.createHttpClient({
                baseURL: Env.get('API_HOST'),
                timeout: 30000,
                headers: {
                    'Authorization': 'Bearer ' + this.onboardingToken
                }
            })
        },

        getIntegrationApiClient: function() {
            if (!this.onboardingToken) return null
            return this.createHttpClient({
                baseURL: Env.get('INTEGRATION_HOST'),
                timeout: 30000,
                headers: {
                    'Authorization': 'Bearer ' + this.onboardingToken
                }
            })
        },

        getTransactionApiClient: function() {
            return this.createHttpClient({
                baseURL: Env.get('TRANSACTION_HOST'),
                timeout: 30000
            })
        },

        loadInitialData: function() {
            var self = this
            var client = self.getIntegrationApiClient()
            if (!client) return Promise.resolve()

            self.loadingSettings = true

            return client.get('/integrations').then(function(resp) {
                var integrations = resp.data || []
                self.integration = integrations.find(function(i) { return i.id === 'protokol-ecommerce' }) || null
                self.paymentsIntegration = integrations.find(function(i) { return i.id === 'protokol-payments' }) || null

                // Set language / currency defaults for Serbian integration
                if (self.isSerbian) {
                    if (self.formData.language === 'en') self.formData.language = 'sr'
                    if (self.formData.currency === 'USD') self.formData.currency = 'RSD'
                }

                // Pre-populate country from integration config if not already set
                if (!self.formData.companyInfo.country && self.predefinedCountry) {
                    self.formData.companyInfo.country = self.predefinedCountry
                }

                // Partner-specific payment defaults
                if (self.isMonriPartner) {
                    if (!self.formData.paymentMethods.includes('credit_card')) {
                        self.formData.paymentMethods.push('credit_card')
                    }
                    self.formData.creditCardProvider = 'monri'
                    if (self.isSerbian) {
                        self.formData.creditCardReceivingMethod = 'chip_card'
                    }
                } else if (self.isChipcardPartner) {
                    if (!self.formData.paymentMethods.includes('credit_card')) {
                        self.formData.paymentMethods.push('credit_card')
                    }
                    self.formData.creditCardProvider = 'chipcard'
                }

                return client.get('/ecomm/user')
            }).then(function(resp) {
                self.ecommSettings = resp.data
                var settings = self.ecommSettings
                if (settings) {
                    if (settings.base_domain) {
                        self.formData.storeUrl = settings.base_domain.split('.')[0]
                        self.storeUrlManuallyEdited = true
                    }
                    if (settings.general && settings.general.store_name) {
                        self.formData.storeName = settings.general.store_name
                    }
                    if (settings.custom_domains && settings.custom_domains.length > 0) {
                        self.formData.customDomains = settings.custom_domains.slice()
                    }
                    if (settings.settings && settings.settings.dev && settings.settings.dev.header && settings.settings.dev.header.logo && settings.settings.dev.header.logo.src) {
                        self.formData.logoBase64 = settings.settings.dev.header.logo.src
                    }
                    if (settings.settings && settings.settings.dev && settings.settings.dev.price && settings.settings.dev.price.default_currency && settings.settings.dev.price.default_currency.id) {
                        self.formData.currency = settings.settings.dev.price.default_currency.id.toUpperCase()
                    }
                    if (settings.settings && settings.settings.dev && settings.settings.dev.cart && settings.settings.dev.cart.payment_methods && settings.settings.dev.cart.payment_methods.methods) {
                        self.formData.paymentMethods = settings.settings.dev.cart.payment_methods.methods.map(function(m) {
                            return typeof m === 'string' ? m : m.id
                        })
                    }
                    if (settings.settings && settings.settings.dev && settings.settings.dev.cart && settings.settings.dev.cart.shipping_methods && settings.settings.dev.cart.shipping_methods.methods) {
                        self.formData.shippingMethods = settings.settings.dev.cart.shipping_methods.methods.map(function(m) {
                            return typeof m === 'string' ? m : m.id
                        })
                    }
                    if (settings.company) {
                        var c = settings.company
                        if (!self.formData.companyInfo.country) self.formData.companyInfo.country = c.country || ''
                        self.formData.companyInfo.pib = c.vat_number || ''
                        self.formData.companyInfo.legalName = c.legal_name || ''
                        self.formData.companyInfo.companyName = c.company_name || ''
                        self.formData.companyInfo.companyAddress = c.address || ''
                        self.formData.companyInfo.companyCity = c.city || ''
                        self.formData.companyInfo.companyPostalCode = c.zip_code || ''
                        self.formData.companyInfo.companyPhone = c.phone ? c.phone.replace('+381', '') : ''
                        self.formData.companyInfo.companyEmail = c.email || ''
                        self.formData.companyInfo.legalRepresentative = c.legal_representative || ''
                        if (c.additional_info) {
                            self.formData.companyInfo.mb = c.additional_info.registration_number || ''
                            self.formData.companyInfo.activityCode = c.additional_info.activity_code || ''
                            self.formData.companyInfo.pdv = c.additional_info.pdv != null ? c.additional_info.pdv : null
                        }
                    }
                }

                // Re-enforce Monri partner after ecommSettings may have overwritten payment methods
                if (self.isMonriPartner) {
                    if (!self.formData.paymentMethods.includes('credit_card')) {
                        self.formData.paymentMethods.push('credit_card')
                    }
                    self.formData.creditCardProvider = 'monri'
                }
                // Always enforce RSD for Serbian stores regardless of stored settings
                if (self.isSerbian) {
                    self.formData.currency = 'RSD'
                }

                // Load Monri credentials if already configured
                if (self.formData.creditCardProvider === 'monri') {
                    return self.loadMonriSettings()
                }
            }).catch(function(err) {
                console.error('Failed to load initial data:', err)
            }).finally(function() {
                self.loadingSettings = false
            })
        },

        loadMonriSettings: function() {
            var self = this
            var client = self.getIntegrationApiClient()
            if (!client) return Promise.resolve()

            return client.get('/payments/monri/settings').then(function(resp) {
                var settings = (resp.data && resp.data.settings) || {}
                if (settings.merchant_key) self.formData.monriMerchantKey = settings.merchant_key
                if (settings.authenticity_token) self.formData.monriAuthenticityToken = settings.authenticity_token
            }).catch(function() {
                // Settings may not exist yet — that's fine
            })
        },

        fetchIntegrationPrice: function() {
            var self = this
            self.loadingPrice = true
            var currency = 'USD'

            var client = self.getTransactionApiClient()
            client.post('/prices', {
                product_type: 'template',
                reference: 'monri-ecomm',
                currency: currency
            }).then(function(resp) {
                var total = resp.data.total_amount
                if (currency === 'RSD') {
                    self.integrationPrice = { formatted: Math.round(total).toLocaleString('sr-RS') + ' RSD' }
                } else {
                    self.integrationPrice = { formatted: '$' + total.toFixed(2) }
                }
            }).catch(function(err) {
                console.error('Failed to fetch integration price:', err)
            }).finally(function() {
                self.loadingPrice = false
            })
        },

        registerUser: function() {
            var self = this
            if (!self.canProceed) return

            self.registering = true
            self.registerError = null

            if (self.registerForm.password !== self.registerForm.confirmPassword) {
                self.registerError = self.$t('registration.passwordMismatch')
                self.registering = false
                return
            }

            var platformClient = self.createHttpClient({
                baseURL: Env.get('API_HOST'),
                timeout: 30000
            })

            platformClient.post('/v1/user/register_legacy', {
                name: self.registerForm.name,
                email: self.registerForm.email,
                password: self.registerForm.password,
                project_name: self.registerForm.projectName,
                products: [{
                    product_type: 'template',
                    reference: 'monri-ecomm',
                    source: 'template',
                    options: {}
                }],
                lang: self.$i18n.locale,
                onboarding: true
            }).then(function(resp) {
                if (resp.data && resp.data.onboarding_token) {
                    self.onboardingToken = resp.data.onboarding_token
                }
                self.currentStep = 2
                self.saveToCache()
                self.loadInitialData()
            }).catch(function(error) {
                if (error.response) {
                    if (error.response.status === 417) {
                        var errors = (error.response.data && error.response.data.errors) || {}
                        var messages = []
                        Object.keys(errors).forEach(function(key) {
                            if (Array.isArray(errors[key])) {
                                messages = messages.concat(errors[key])
                            } else {
                                messages.push(errors[key])
                            }
                        })
                        self.registerError = messages.join(', ') || self.$t('registration.validationError')
                    } else {
                        self.registerError = (error.response.data && error.response.data.message) || self.$t('unexpectedError')
                    }
                } else {
                    self.registerError = self.$t('unexpectedError')
                }
            }).finally(function() {
                self.registering = false
            })
        },

        refreshIntegrations: function() {
            var self = this
            var client = self.getIntegrationApiClient()
            if (!client) return
            client.get('/integrations').then(function(resp) {
                var integrations = resp.data || []
                self.integration = integrations.find(function(i) { return i.id === 'protokol-ecommerce' }) || null
                self.paymentsIntegration = integrations.find(function(i) { return i.id === 'protokol-payments' }) || null
            }).catch(function(err) {
                console.warn('Failed to refresh integrations:', err)
            })
        },

        saveStepData: function(stepIndex) {
            var self = this
            var client = self.getIntegrationApiClient()
            if (!client) return

            var payload = {}

            switch(stepIndex) {
                case 2: // Company Info
                    payload = {
                        company: {
                            country: self.formData.companyInfo.country,
                            vat_number: self.formData.companyInfo.pib,
                            legal_name: self.formData.companyInfo.legalName,
                            company_name: self.formData.companyInfo.companyName,
                            address: self.formData.companyInfo.companyAddress,
                            city: self.formData.companyInfo.companyCity,
                            zip_code: self.formData.companyInfo.companyPostalCode,
                            phone: self.isSerbian ? ('+381' + self.formData.companyInfo.companyPhone) : self.formData.companyInfo.companyPhone,
                            email: self.formData.companyInfo.companyEmail,
                            legal_representative: self.formData.companyInfo.legalRepresentative,
                            additional_info: {
                                registration_number: self.formData.companyInfo.mb,
                                activity_code: self.formData.companyInfo.activityCode,
                                pdv: self.formData.companyInfo.pdv
                            }
                        }
                    }
                    break
                case 3: // Store Configuration
                    var selectedDesc = self.merchantDescriptionOptions.find(function(opt) {
                        return opt.value === self.formData.merchantDescription
                    })
                    payload = {
                        general: { store_name: self.formData.storeName },
                        base_domain: self.formData.storeUrl + '.' + self.shopDomain
                    }
                    if (selectedDesc) {
                        payload.merchant_description = selectedDesc.text
                        payload.mcc = selectedDesc.mcc
                    }
                    break
                case 4: // Product Type & Shipping
                    payload = {
                        product_types: [self.formData.productType],
                        settings: {
                            dev: {
                                cart: {
                                    shipping_methods: {
                                        methods: self.formData.shippingMethods.map(function(id) {
                                            var sm = self.shippingMethods.find(function(m) { return m.value === id })
                                            return { id: id, name: sm ? self.$t(sm.textKey) : id }
                                        })
                                    }
                                }
                            }
                        }
                    }
                    break
                case 5: // Payment Methods
                    var paymentProviders = {}
                    if (self.formData.paymentMethods.includes('credit_card')) {
                        if (self.formData.creditCardProvider === 'monri' || self.formData.creditCardProvider === 'chipcard') {
                            paymentProviders = { options: { id: 'monri', name: 'Monri' } }
                        }
                    }
                    payload = {
                        settings: {
                            dev: {
                                cart: {
                                    payment_methods: {
                                        methods: self.formData.paymentMethods.map(function(id) {
                                            var names = {
                                                'credit_card': self.formData.language === 'sr' ? 'Platna kartica' : 'Credit Card',
                                                'bank_transfer': self.formData.language === 'sr' ? 'Uplata na račun' : 'Bank Transfer',
                                                'cash_on_delivery': self.formData.language === 'sr' ? 'Plaćanje pouzećem' : 'Cash on Delivery'
                                            }
                                            return { id: id, name: names[id] || id }
                                        })
                                    },
                                    payment_providers: paymentProviders
                                }
                            }
                        }
                    }
                    break
                case 6: // Documentation (TOS)
                    if (self.formData.termsOfService) {
                        payload = {
                            documentation: {
                                terms_of_service: self.formData.termsOfService
                            }
                        }
                    }
                    break
            }

            if (Object.keys(payload).length === 0) return

            client.patch('/ecomm/user', payload).catch(function(err) {
                console.warn('Failed to save step data:', err)
            })
        },

        fetchTosPreview: function() {
            var self = this
            self.loadingTosPreview = true
            self.tosPreviewHtml = ''

            var client = self.getIntegrationApiClient()
            if (!client) {
                self.loadingTosPreview = false
                return
            }

            var payload = {
                company: {
                    country: self.formData.companyInfo.country,
                    vat_number: self.formData.companyInfo.pib,
                    legal_name: self.formData.companyInfo.legalName,
                    company_name: self.formData.companyInfo.companyName,
                    address: self.formData.companyInfo.companyAddress,
                    city: self.formData.companyInfo.companyCity,
                    zip_code: self.formData.companyInfo.companyPostalCode,
                    phone: self.isSerbian ? ('+381' + self.formData.companyInfo.companyPhone) : self.formData.companyInfo.companyPhone,
                    email: self.formData.companyInfo.companyEmail
                },
                settings: {
                    cart: {
                        payment_methods: {
                            methods: self.formData.paymentMethods.map(function(id) {
                                var names = {
                                    'credit_card': self.formData.language === 'sr' ? 'Platna kartica' : 'Credit Card',
                                    'bank_transfer': self.formData.language === 'sr' ? 'Uplata na račun' : 'Bank Transfer',
                                    'cash_on_delivery': self.formData.language === 'sr' ? 'Plaćanje pouzećem' : 'Cash on Delivery'
                                }
                                return { id: id, name: names[id] || id }
                            })
                        }
                    },
                    price: {
                        default_currency: {
                            id: self.formData.currency,
                            name: self.formData.currency
                        }
                    }
                }
            }

            client.post('/ecomm/terms-of-service/preview', payload).then(function(resp) {
                self.tosPreviewHtml = resp.data.html || ''
            }).catch(function(err) {
                console.error('Failed to fetch TOS preview:', err)
                self.tosPreviewHtml = ''
            }).finally(function() {
                self.loadingTosPreview = false
            })
        },

        nextStep: function() {
            if (this.canProceed && this.currentStep < 8) {
                // Show legal confirmation when leaving company info step for Serbian stores
                if (this.currentStep === 2 && this.isSerbian) {
                    this.showLegalModal = true
                    return
                }
                var leavingStep = this.currentStep
                this.currentStep++
                this.saveToCache()
                this.refreshIntegrations()
                this.saveStepData(leavingStep)
                if (this.currentStep === 6) {
                    this.fetchTosPreview()
                }
            }
        },

        handleLegalConfirmation: function() {
            this.showLegalModal = false
            var leavingStep = this.currentStep
            this.currentStep++
            this.saveToCache()
            this.refreshIntegrations()
            this.saveStepData(leavingStep)
            if (this.currentStep === 6) {
                this.fetchTosPreview()
            }
        },

        prevStep: function() {
            // Don't go back past step 2 (registration is already done)
            if (this.currentStep > 2) {
                this.currentStep--
                this.saveToCache()
                this.refreshIntegrations()
            } else if (this.currentStep <= 1) {
                this.currentStep--
            }
        },

        togglePaymentMethod: function(method) {
            var index = this.formData.paymentMethods.indexOf(method)
            if (index > -1) {
                this.formData.paymentMethods.splice(index, 1)
                if (method === 'credit_card') {
                    this.formData.creditCardReceivingMethod = null
                    this.formData.companyInfo.selectedBankAccount = null
                }
            } else {
                this.formData.paymentMethods.push(method)
            }
            this.saveToCache()
        },

        toggleShippingMethod: function(method) {
            var index = this.formData.shippingMethods.indexOf(method)
            if (index > -1) {
                this.formData.shippingMethods.splice(index, 1)
            } else {
                this.formData.shippingMethods.push(method)
            }
            this.saveToCache()
        },

        onLogoChange: function(event) {
            var self = this
            var file = event.target.files[0]
            if (!file) {
                self.formData.logoBase64 = ''
                self.logoSizeError = false
                return
            }

            if (file.size > 512000) {
                self.formData.logoBase64 = ''
                self.logoFile = null
                self.logoSizeError = true
                return
            }

            self.logoSizeError = false
            var reader = new FileReader()
            reader.onload = function(e) {
                self.formData.logoBase64 = e.target.result
                self.saveToCache()
            }
            reader.readAsDataURL(file)
        },

        onPibInput: function(value) {
            var numericValue = value.replace(/\D/g, '')
            this.formData.companyInfo.pib = numericValue
            this.pibError = null

            if (numericValue.length > 0 && numericValue.length < 9) {
                this.pibError = this.$t('ecommerce.companyInfo.pibLengthError')
            } else if (numericValue.length === 9) {
                this.fetchCompanyData(numericValue)
            }
        },

        fetchCompanyData: function(pib) {
            var self = this
            self.fetchingCompanyData = true
            self.pibError = null

            // PIB lookup is a public API via the platform proxy
            var client = self.getApiClient()
            if (!client) {
                // Before registration, use a plain client
                client = self.createHttpClient({
                    baseURL: Env.get('API_HOST'),
                    timeout: 30000
                })
            }

            client.get('/luma/integrations/karadjordje/v1/services/nbs/search?vatNumber=' + pib).then(function(resp) {
                var data = resp.data
                if (data) {
                    self.formData.companyInfo.legalName = data.name || ''
                    self.formData.companyInfo.companyName = (data.name || '').trim().slice(-40).trim()
                    self.formData.companyInfo.companyAddress = data.address || ''
                    self.formData.companyInfo.companyCity = data.place || ''
                    self.formData.companyInfo.mb = data.mb || ''
                    self.formData.companyInfo.activityCode = data.activity_code || ''

                    if (data.banks && Array.isArray(data.banks)) {
                        self.formData.companyInfo.bankAccounts = data.banks
                    } else {
                        self.formData.companyInfo.bankAccounts = []
                    }

                    if (self.formData.companyInfo.bankAccounts.length === 1) {
                        self.formData.companyInfo.selectedBankAccount = 0
                    }

                    self.saveToCache()
                }
            }).catch(function(error) {
                console.error('Error fetching company data:', error)
                self.pibError = self.$t('ecommerce.companyInfo.pibFetchError')
            }).finally(function() {
                self.fetchingCompanyData = false
            })
        },

        validateDomainDebounced: function() {
            var self = this
            if (self.validateDomainTimer) {
                clearTimeout(self.validateDomainTimer)
            }
            self.validateDomainTimer = setTimeout(function() {
                self.validateDomainTimer = null
                self.validateDomain()
            }, 500)
        },

        validateDomain: function() {
            var self = this
            if (!self.formData.storeUrl) {
                self.domainValidationError = null
                self.domainValidationSuccess = false
                return
            }

            self.validatingDomain = true
            self.domainValidationError = null
            self.domainValidationSuccess = false

            var client = self.getIntegrationApiClient()
            if (!client) {
                self.validatingDomain = false
                return
            }

            var fullDomain = self.formData.storeUrl + '.' + self.shopDomain
            client.post('/ecomm/base-domain/check', {
                domain: fullDomain
            }).then(function() {
                self.domainValidationError = null
                self.domainValidationSuccess = true
            }).catch(function(err) {
                self.domainValidationSuccess = false
                if (err.response && err.response.data && err.response.data.message) {
                    self.domainValidationError = err.response.data.message
                } else {
                    self.domainValidationError = self.$t('ecommerce.store.domainUnavailable')
                }
            }).finally(function() {
                self.validatingDomain = false
            })
        },

        onBankInstructionChange: function(event) {
            var self = this
            var file = event.target.files[0]
            if (!file) {
                self.formData.bankTransferInstructionPdf = ''
                return
            }
            if (file.type === 'application/pdf') {
                var reader = new FileReader()
                reader.onload = function(e) {
                    self.formData.bankTransferInstructionPdf = e.target.result
                    self.saveToCache()
                }
                reader.readAsDataURL(file)
            }
        },

        onTermsOfServiceChange: function(event) {
            var self = this
            var file = event.target.files[0]
            if (!file) {
                self.formData.termsOfService = ''
                return
            }
            if (file.type === 'application/pdf') {
                var reader = new FileReader()
                reader.onload = function(e) {
                    self.formData.termsOfService = e.target.result
                    self.formData.useAutoGeneratedDocs = false
                    self.saveToCache()
                }
                reader.readAsDataURL(file)
            }
        },

        addCustomDomain: function() {
            if (this.newDomain && this.formData.customDomains.indexOf(this.newDomain) === -1) {
                this.formData.customDomains.push(this.newDomain)
                this.newDomain = ''
                this.saveToCache()
            }
        },

        removeDomain: function(index) {
            this.formData.customDomains.splice(index, 1)
            this.saveToCache()
        },

        getLanguageText: function(langValue) {
            var lang = this.languageOptions.find(function(l) { return l.value === langValue })
            return lang ? lang.text : langValue
        },

        getMerchantDescriptionText: function() {
            var self = this
            var merchant = self.merchantDescriptionOptions.find(function(m) { return m.value === self.formData.merchantDescription })
            if (!merchant) return self.formData.merchantDescription
            return self.formData.language === 'sr' ? merchant.text_sr : merchant.text
        },

        getPaymentMethodLabel: function(method) {
            var keys = {
                'credit_card': 'ecommerce.payment.paymentCard',
                'bank_transfer': 'ecommerce.payment.bankTransfer',
                'cash_on_delivery': 'ecommerce.payment.cashOnDelivery'
            }
            return keys[method] ? this.$t(keys[method]) : method
        },

        getPaymentMethodIcon: function(method) {
            var icons = {
                'credit_card': 'credit-card',
                'bank_transfer': 'bank',
                'cash_on_delivery': 'cash'
            }
            return icons[method] || 'credit-card'
        },

        getProductTypeLabel: function(type) {
            var self = this
            var pt = self.productTypes.find(function(p) { return p.value === type })
            return pt ? self.$t(pt.textKey) : type
        },

        getProductTypeIcon: function(type) {
            var icons = {
                'physical': 'box-seam',
                'digital': 'file-earmark-code',
                'service': 'tools'
            }
            return icons[type] || 'box-seam'
        },

        getShippingMethodLabel: function(method) {
            var self = this
            var sm = self.shippingMethods.find(function(m) { return m.value === method })
            return sm ? self.$t(sm.textKey) : method
        },

        getShippingMethodIcon: function(method) {
            var icons = {
                'delivery': 'truck',
                'pickup': 'shop'
            }
            return icons[method] || 'truck'
        },

        completeOnboarding: function() {
            var self = this
            self.loading = true

            var client = self.getIntegrationApiClient()
            if (!client) {
                self.loading = false
                return
            }

            var languageLabel = self.languageOptions.find(function(l) { return l.value === self.formData.language })

            // Compute payment_providers: chipcard and monri are the same company — always use monri
            var paymentProviders = {}
            if (self.formData.paymentMethods.includes('credit_card')) {
                if (self.formData.creditCardReceivingMethod === 'chip_card' || self.formData.creditCardProvider === 'chipcard' || self.formData.creditCardProvider === 'monri') {
                    paymentProviders = { options: { id: 'monri', name: 'Monri' } }
                }
            }

            var devSettings = {
                maintenance: false,
                header: {
                    logo: {
                        position: 1,
                        src: self.formData.logoBase64,
                        alt: 'Store Logo',
                        width: 120,
                        height: 40
                    }
                },
                cart: {
                    payment_methods: {
                        methods: self.formData.paymentMethods.map(function(id) {
                            var names = {
                                'credit_card': self.formData.language === 'sr' ? 'Platna kartica' : 'Credit Card',
                                'bank_transfer': self.formData.language === 'sr' ? 'Uplata na račun' : 'Bank Transfer',
                                'cash_on_delivery': self.formData.language === 'sr' ? 'Plaćanje pouzećem' : 'Cash on Delivery'
                            }
                            return { id: id, name: names[id] || id }
                        })
                    },
                    shipping_methods: {
                        methods: self.formData.shippingMethods.map(function(id) {
                            var sm = self.shippingMethods.find(function(m) { return m.value === id })
                            return {
                                id: id,
                                name: sm ? self.$t(sm.textKey) : id
                            }
                        })
                    },
                    payment_providers: paymentProviders
                },
                price: {
                    default_currency: {
                        id: self.formData.currency,
                        name: self.formData.currency
                    }
                },
                i18n: {
                    default_locale: {
                        id: self.formData.language,
                        name: languageLabel ? languageLabel.text : self.formData.language
                    },
                    locales: self.languageOptions.map(function(l) {
                        return { id: l.value, name: l.text }
                    })
                }
            }

            var selectedMerchantDesc = self.merchantDescriptionOptions.find(function(opt) {
                return opt.value === self.formData.merchantDescription
            })

            var settingsPayload = {
                general: {
                    store_name: self.formData.storeName
                },
                base_domain: self.formData.storeUrl + '.' + self.shopDomain,
                product_types: [self.formData.productType],
                company: {
                    country: self.formData.companyInfo.country,
                    vat_number: self.formData.companyInfo.pib,
                    legal_name: self.formData.companyInfo.legalName,
                    company_name: self.formData.companyInfo.companyName,
                    address: self.formData.companyInfo.companyAddress,
                    city: self.formData.companyInfo.companyCity,
                    zip_code: self.formData.companyInfo.companyPostalCode,
                    phone: self.isSerbian ? ('+381' + self.formData.companyInfo.companyPhone) : self.formData.companyInfo.companyPhone,
                    email: self.formData.companyInfo.companyEmail,
                    legal_representative: self.formData.companyInfo.legalRepresentative,
                    additional_info: {
                        registration_number: self.formData.companyInfo.mb,
                        activity_code: self.formData.companyInfo.activityCode,
                        pdv: self.formData.companyInfo.pdv
                    }
                },
                settings: {
                    dev: devSettings,
                    live: devSettings
                }
            }

            if (selectedMerchantDesc) {
                settingsPayload.merchant_description = selectedMerchantDesc.text
                settingsPayload.mcc = selectedMerchantDesc.mcc
                if (selectedMerchantDesc.risk) {
                    settingsPayload.risk_level = selectedMerchantDesc.risk
                }
            }

            client.patch('/ecomm/user', settingsPayload).then(function() {
                // Save Monri credentials if applicable
                if (self.formData.creditCardProvider === 'monri' &&
                    self.formData.monriMerchantKey && self.formData.monriAuthenticityToken) {
                    return client.post('/payments/monri/settings', {
                        merchant_key: self.formData.monriMerchantKey,
                        authenticity_token: self.formData.monriAuthenticityToken
                    })
                }
            }).then(function() {
                return client.post('/ecomm/user/complete-onboarding')
            }).then(function() {
                self.clearCache()
                self.currentStep = 8
            }).catch(function(err) {
                console.error('Onboarding error:', err)
                self.$bvToast.toast(self.$t('unexpectedError'), {
                    variant: 'danger',
                    title: self.$t('error')
                })
            }).finally(function() {
                self.loading = false
            })
        },

        saveToCache: function() {
            var cacheData = {
                currentStep: this.currentStep,
                formData: this.formData,
                registerForm: this.registerForm,
                onboardingToken: this.onboardingToken,
                timestamp: new Date().toISOString()
            }
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData))
        },

        getCachedData: function() {
            try {
                var cached = localStorage.getItem(CACHE_KEY)
                if (cached) {
                    return JSON.parse(cached)
                }
            } catch (err) {
                console.error('Failed to parse cached data:', err)
            }
            return null
        },

        clearCache: function() {
            localStorage.removeItem(CACHE_KEY)
        },

        restoreFromCache: function() {
            var cachedData = this.getCachedData()
            if (cachedData && cachedData.onboardingToken) {
                this.onboardingToken = cachedData.onboardingToken
                this.currentStep = cachedData.currentStep || 2
                if (cachedData.formData) {
                    // Merge cached form data
                    var self = this
                    Object.keys(cachedData.formData).forEach(function(key) {
                        if (key === 'companyInfo' && cachedData.formData[key]) {
                            Object.keys(cachedData.formData[key]).forEach(function(subKey) {
                                self.formData.companyInfo[subKey] = cachedData.formData[key][subKey]
                            })
                        } else {
                            self.formData[key] = cachedData.formData[key]
                        }
                    })
                }
                if (cachedData.registerForm) {
                    this.registerForm = cachedData.registerForm
                }

                return true
            }
            return false
        }
    },

    mounted: function() {
        // Try to restore from cache (user might have refreshed after registration)
        var restored = this.restoreFromCache()

        // Fetch pricing (public endpoint, no auth required)
        //this.fetchIntegrationPrice()

        if (restored && this.onboardingToken) {
            // Page refresh after registration — reload integration data using existing token
            this.loadInitialData()
            // Fetch TOS preview if resuming on the documentation step
            if (this.currentStep === 6) {
                this.fetchTosPreview()
            }
        } else if (!restored) {
            // Fresh first visit — apply browser locale defaults
            if (navigator.language && navigator.language.startsWith('sr')) {
                this.formData.language = 'sr'
                this.formData.companyInfo.country = 'RS'
                this.formData.currency = 'RSD'
            }
        }
    },

    beforeDestroy: function() {},

    i18n: {
        messages: {
            en_US: {
                back: 'Back',
                continue: 'Continue',
                finish: 'Finish Setup',
                saving: 'Saving...',
                cancel: 'Cancel',
                error: 'Error',
                unexpectedError: 'An unexpected error occurred. Please try again.',
                registration: {
                    stepLabel: 'Account',
                    title: 'Create Your Account',
                    description: 'Register to get started with your online store',
                    name: 'Full Name',
                    namePlaceholder: 'John Doe',
                    email: 'Email Address',
                    emailPlaceholder: 'john@example.com',
                    projectName: 'Company / Project Name',
                    projectNamePlaceholder: 'My Company',
                    password: 'Password',
                    passwordPlaceholder: 'Min. 7 characters',
                    confirmPassword: 'Confirm Password',
                    confirmPasswordPlaceholder: 'Repeat your password',
                    createAccount: 'Create Account',
                    passwordMismatch: 'Passwords do not match',
                    validationError: 'Please check your input and try again'
                },
                complete: {
                    title: 'Setup Complete!',
                    description: 'Your online store has been set up successfully. We are preparing everything for you.',
                    checkEmail: 'Please check your email to confirm your account and access your dashboard.',
                    backToLogin: 'Go to Login',
                    chipcardTitle: 'You are almost done!',
                    chipcardDescription: 'In order to prepare for the next phase of onboarding you will be contacted by Chipcard. Please prepare the following documentation:',
                    chipcardDoc1: 'A valid personal document (ID card or passport).',
                    chipcardDoc2: 'If the ID card has a chip or does not contain address information, prepare a scanned ID card or one of the following documents: property tax determination, vehicle registration, phone bill, utility bill, etc.',
                    chipcardDoc3: 'Please prepare an extract from the APR register not older than three months.',
                    chipcardDoc4: 'In case of establishing a contractual relationship through a proxy (prokurist), it is necessary to come in person to the Chip Card address.'
                },
                ecommerce: {
                    loading: 'Loading your store settings...',

                    auth: {
                        sessionExpiredTitle: 'Session expired',
                        sessionExpiredDescription: 'Your onboarding session is no longer valid. Sign in to continue where you left off.',
                        signInToContinue: 'Sign in to continue'
                    },

                    steps: {
                        welcome: 'Welcome',
                        store: 'Settings',
                        companyInfo: 'Company Info',
                        shipping: 'Category',
                        payment: 'Payment',
                        documentation: 'Documentation',
                        summary: 'Summary'
                    },
                    welcome: {
                        title: 'Welcome to E-commerce',
                        description: 'Set up your online store in just a few steps. This integration will help you manage products, orders, and customers efficiently.',
                        feature1: 'Choose your webshop name, get a free domain',
                        feature2: 'Manage products, prices, and inventory in one place',
                        feature3: 'Track orders, deliveries, and payments in real-time',
                        feature4: 'Analyze sales and customer data',
                        feature5: 'Accept online payments',
                        pricingTitle: 'Monthly Subscription',
                        perMonth: 'per month',
                        confirmationTitle: 'Legal Confirmation',
                        confirmationText: 'I, under full material and criminal responsibility, confirm that the information is accurate and that I am the legal representative of the company.',
                        confirmButton: 'I Confirm'
                    },
                    store: {
                        title: 'Store Configuration',
                        description: 'Set up your basic store information',
                        storeName: 'Store Name',
                        storeNamePlaceholder: 'My Awesome Shop',
                        storeUrl: 'Webshop URL',
                        storeUrlPlaceholder: 'my-store',
                        storeUrlHint: 'Your store will be available at: {domain}',
                        domainUnavailable: 'This domain is already taken',
                        logo: 'Store Logo',
                        logoPlaceholder: 'Choose a file or drop it here...',
                        logoDropPlaceholder: 'Drop file here...',
                        logoHint: 'Upload your store logo (recommended size: 200x200px, max 500KB)',
                        logoSizeError: 'Logo file is too large. Maximum allowed size is 500KB.',
                        currency: 'Default Currency',
                        language: 'Main Language',
                        merchantDescription: 'Description'
                    },
                    companyInfo: {
                        title: 'Company Information',
                        description: 'Provide your company details for legal compliance',
                        country: 'Country',
                        countryPlaceholder: 'Select country...',
                        pib: 'PIB (Tax ID)',
                        pibPlaceholder: '123456789',
                        pibHint: 'Enter 9 digits to auto-fill company information',
                        pibLengthError: 'PIB must be exactly 9 digits',
                        pibFetchError: 'Could not fetch company data. Please enter manually.',
                        legalName: 'Legal Name',
                        legalNamePlaceholder: 'My Company d.o.o.',
                        companyName: 'Company Name',
                        companyNamePlaceholder: 'My Company',
                        companyNameHint: 'Display name for your store (max 40 characters)',
                        companyAddress: 'Address',
                        companyAddressPlaceholder: 'Street name and number',
                        companyCity: 'City',
                        companyCityPlaceholder: 'Belgrade',
                        companyPostalCode: 'Postal Code',
                        companyPostalCodePlaceholder: '11000',
                        companyPhone: 'Phone',
                        companyPhonePlaceholder: '+381 11 123 4567',
                        companyEmail: 'Email Address',
                        companyEmailPlaceholder: 'info@company.com',
                        legalRepresentative: 'Legal Representative',
                        legalRepresentativePlaceholder: 'Full name of legal representative',
                        mb: 'MB (Registration Number)',
                        mbPlaceholder: 'Auto-filled from PIB',
                        activityCode: 'Activity Code',
                        activityCodePlaceholder: 'Auto-filled from PIB',
                        bankAccount: 'Bank Account for Credit Card Deposits',
                        selectBankAccount: 'Select a bank account',
                        bankAccountHint: 'Choose the bank account where credit card payments will be deposited',
                        pdvQuestion: 'Are you registered for VAT?',
                        pdvYes: 'Yes',
                        pdvNo: 'No'
                    },
                    domains: {
                        description: 'Add your own custom domains for your store (optional)',
                        setupCustomDomain: 'Do you want to set up your own domain?',
                        add: 'Add',
                        placeholder: 'example.com'
                    },
                    payment: {
                        title: 'Payment Methods',
                        description: 'Configure how customers will pay for their orders',
                        choosePaymentType: 'Choose payment methods',
                        paymentCard: 'Cashless payment',
                        chooseProvider: 'Choose payment provider',
                        paymentProcessor: 'Payment processor',
                        merchantKey: 'Merchant Key',
                        merchantKeyPlaceholder: 'Enter your Merchant Key',
                        authenticityToken: 'Authenticity Token',
                        authenticityTokenPlaceholder: 'Enter your Authenticity Token',
                        monriCredentials: 'Monri Settings',
                        creditCard: 'Credit/Debit Card',
                        bankTransfer: 'Bank Transfer',
                        cashOnDelivery: 'Cash on Delivery',
                        bankTransferInstruction: 'Payment Instructions (PDF)',
                        bankTransferInstructionHint: 'Upload a PDF with bank account details and payment instructions for customers',
                        bankTransferInstructionUploadPrompt: 'Click to upload payment instructions PDF',
                        bankTransferInstructionUploaded: 'Payment instructions PDF uploaded',
                        setupLater: 'I will set this up later',
                        required: 'Required',
                        monriPartnerCredentialsInfo: 'Your Monri credentials are managed by your service partner.'
                    },
                    shipping: {
                        title: 'Category',
                        description: 'Choose your store category',
                        productType: 'Type of Products',
                        methods: 'Delivery Methods',
                        physical: 'Physical Products',
                        digital: 'Digital Products',
                        services: 'Services',
                        delivery: 'Delivery to address',
                        pickupFromShop: 'Pickup from shop'
                    },
                    documentation: {
                        title: 'Terms of Service',
                        description: 'Review your auto-generated terms of service or upload your own.',
                        autoGeneratedInfo: 'Your Terms of Service are automatically generated based on your store settings.',
                        termsOfService: 'Terms of Service',
                        uploadPrompt: 'Click here or drag and drop file to upload',
                        uploadHint: 'PDF format only',
                        customFileUploaded: 'Custom document uploaded',
                        autoGeneratedPreview: 'Document will be automatically generated based on your store configuration',
                        tosPreviewInfo: 'This is how your Terms of Service will appear to your customers.',
                        tosPreviewError: 'Could not load Terms of Service preview.',
                        uploadCustom: 'Or upload your own custom Terms of Service'
                    },
                    summary: {
                        title: 'All Set!',
                        description: 'Review your configuration and complete the setup',
                        storeInfo: 'Store Information',
                        companyInfo: 'Company Information',
                        domainsInfo: 'Custom Domains',
                        paymentInfo: 'Payment Methods',
                        shippingInfo: 'Category & Shipping',
                        autoGenerated: 'Auto-generated',
                        noShippingRequired: 'No physical shipping required'
                    }
                },
                support: {
                    stuck: 'Are you stuck? Contact support'
                }
            },
            sr: {
                back: 'Nazad',
                continue: 'Nastavi',
                finish: 'Završi podešavanje',
                saving: 'Čuvam...',
                cancel: 'Otkaži',
                error: 'Greška',
                unexpectedError: 'Došlo je do neočekivane greške. Pokušajte ponovo.',
                registration: {
                    stepLabel: 'Nalog',
                    title: 'Kreirajte vaš nalog',
                    description: 'Registrujte se da biste pokrenuli svoju online prodavnicu',
                    name: 'Ime i prezime',
                    namePlaceholder: 'Petar Petrović',
                    email: 'Email adresa',
                    emailPlaceholder: 'petar@example.com',
                    projectName: 'Naziv kompanije / projekta',
                    projectNamePlaceholder: 'Moja kompanija',
                    password: 'Lozinka',
                    passwordPlaceholder: 'Min. 7 karaktera',
                    confirmPassword: 'Potvrdite lozinku',
                    confirmPasswordPlaceholder: 'Ponovite lozinku',
                    createAccount: 'Kreiraj nalog',
                    passwordMismatch: 'Lozinke se ne poklapaju',
                    validationError: 'Proverite unete podatke i pokušajte ponovo'
                },
                complete: {
                    title: 'Podešavanje završeno!',
                    description: 'Vaša online prodavnica je uspešno podešena. Pripremamo sve za vas.',
                    checkEmail: 'Proverite vaš email da biste potvrdili nalog i pristupili kontrolnoj tabli.',
                    backToLogin: 'Idi na prijavu',
                    chipcardTitle: 'Još samo malo!',
                    chipcardDescription: 'Kako biste se pripremili za sledeću fazu ugovaranja, kontaktiraće vas Chipcard. Molimo vas da pripremite sledeću dokumentaciju:',
                    chipcardDoc1: 'Validan lični dokument (lična karta ili pasoš).',
                    chipcardDoc2: 'U slučaju da je lična karta sa čipom ili da dokument ne sadrži podatke o adresi, pripremiti očitanu ličnu kartu ili neki od sledećih dokumenata: rešenje o utvrđivanju poreza na imovinu, saobraćajna dozvola, račun za telefon, komunalne usluge i sl.',
                    chipcardDoc3: 'Molimo vas da pripremite Izvod iz registra APR-a ne stariji od tri meseca.',
                    chipcardDoc4: 'U slučaju uspostavljanja ugovornog odnosa preko prokuriste potrebno je lično doći na adresu Chip Card-a.'
                },
                ecommerce: {
                    loading: 'Učitavam podešavanja prodavnice...',

                    auth: {
                        sessionExpiredTitle: 'Sesija je istekla',
                        sessionExpiredDescription: 'Vaša onboarding sesija više nije važeća. Prijavite se da biste nastavili tamo gde ste stali.',
                        signInToContinue: 'Prijavite se za nastavak'
                    },

                    steps: {
                        welcome: 'Dobrodošli',
                        store: 'Podešavanja',
                        companyInfo: 'Kompanija',
                        shipping: 'Kategorija',
                        payment: 'Plaćanje',
                        documentation: 'Dokumentacija',
                        summary: 'Pregled'
                    },
                    welcome: {
                        title: 'Tvoj webshop u par koraka',
                        description: 'Popunite ovu brzu formu u par koraka i dobijate webshop spreman za rad',
                        feature1: 'Izaberite ime svog webshopa, domen dobijate besplatno',
                        feature2: 'Upravljajte proizvodima, cenama i zalihama na jednom mestu',
                        feature3: 'Pratite porudžbine, dostave i plaćanja u realnom vremenu',
                        feature4: 'Analizirajte prodaju i podatke o kupcima',
                        feature5: 'Prihvat online plaćanja',
                        pricingTitle: 'Mesečna pretplata',
                        perMonth: 'mesečno',
                        confirmationTitle: 'Pravna potvrda',
                        confirmationText: 'Ovim potvrđujem pod punom zakonskom odgovornošću da su navedeni podaci istiniti kao ovlašćeni zastupnik privrednog subjekta',
                        confirmButton: 'Potvrđujem'
                    },
                    store: {
                        title: 'Podešavanje prodavnice',
                        description: 'Počnimo sa podešavanjem osnovnih informacija o vašoj prodavnici',
                        storeName: 'Naziv prodavnice',
                        storeNamePlaceholder: 'Moja prodavnica',
                        storeUrl: 'Webshop URL',
                        storeUrlPlaceholder: 'moja-prodavnica',
                        storeUrlHint: 'Vaša prodavnica će biti dostupna na: {domain}',
                        domainUnavailable: 'Ovaj domen je već zauzet',
                        logo: 'Logo prodavnice',
                        logoPlaceholder: 'Izaberite fajl ili ga prevucite ovde...',
                        logoDropPlaceholder: 'Prevucite fajl ovde...',
                        logoHint: 'Otpremite logo vaše prodavnice (preporučena veličina: 200x200px, maks. 500KB)',
                        logoSizeError: 'Fajl logotipa je prevelik. Maksimalna dozvoljena veličina je 500KB.',
                        currency: 'Osnovna valuta',
                        language: 'Glavni jezik',
                        merchantDescription: 'Opis'
                    },
                    companyInfo: {
                        title: 'Informacije o kompaniji',
                        description: 'Unesite podatke o vašoj kompaniji za pravnu usaglašenost',
                        country: 'Država',
                        countryPlaceholder: 'Izaberite državu...',
                        pib: 'PIB',
                        pibPlaceholder: '123456789',
                        pibHint: 'Unesite 9 cifara za automatsko popunjavanje podataka',
                        pibLengthError: 'PIB mora imati tačno 9 cifara',
                        pibFetchError: 'Nije moguće preuzeti podatke o kompaniji. Molimo unesite ručno.',
                        legalName: 'Registrovani naziv',
                        legalNamePlaceholder: 'Moja kompanija d.o.o.',
                        companyName: 'Naziv kompanije',
                        companyNamePlaceholder: 'Moja kompanija',
                        companyNameHint: 'Naziv za prikaz u prodavnici (maks. 40 karaktera)',
                        companyAddress: 'Adresa',
                        companyAddressPlaceholder: 'Naziv ulice i broj',
                        companyCity: 'Grad',
                        companyCityPlaceholder: 'Beograd',
                        companyPostalCode: 'Poštanski broj',
                        companyPostalCodePlaceholder: '11000',
                        companyPhone: 'Telefon',
                        companyPhonePlaceholder: '+381 11 123 4567',
                        companyEmail: 'Email adresa',
                        companyEmailPlaceholder: 'info@kompanija.com',
                        legalRepresentative: 'Zakonski zastupnik',
                        legalRepresentativePlaceholder: 'Puno ime zakonskog zastupnika',
                        mb: 'MB (Matični broj)',
                        mbPlaceholder: 'Automatski popunjeno iz PIB-a',
                        activityCode: 'Šifra delatnosti',
                        activityCodePlaceholder: 'Automatski popunjeno iz PIB-a',
                        bankAccount: 'Bankovni račun za uplatu karticom',
                        selectBankAccount: 'Izaberite bankovni račun',
                        bankAccountHint: 'Izaberite bankovni račun na koji će biti uplaćena plaćanja karticom',
                        pdvQuestion: 'Da li ste u sistemu PDV-a?',
                        pdvYes: 'Da',
                        pdvNo: 'Ne'
                    },
                    domains: {
                        description: 'Dodajte vaše prilagođene domene za prodavnicu (opciono)',
                        setupCustomDomain: 'Da li želite da podesite sopstveni domen?',
                        add: 'Dodaj',
                        placeholder: 'primer.com'
                    },
                    payment: {
                        title: 'Načini plaćanja',
                        description: 'Podesite kako će kupci plaćati svoje narudžbine',
                        choosePaymentType: 'Izaberite načine plaćanja',
                        paymentCard: 'Bezgotovinsko plaćanje',
                        chooseProvider: 'Izaberite pružaoca plaćanja',
                        paymentProcessor: 'Procesor platnih transakcija',
                        merchantKey: 'Merchant Key',
                        merchantKeyPlaceholder: 'Unesite vaš Merchant Key',
                        authenticityToken: 'Authenticity Token',
                        authenticityTokenPlaceholder: 'Unesite vaš Authenticity Token',
                        monriCredentials: 'Monri podešavanja',
                        creditCard: 'Kreditna/debitna kartica',
                        bankTransfer: 'Uplata na račun',
                        cashOnDelivery: 'Plaćanje pouzećem',
                        bankTransferInstruction: 'Uputstvo za plaćanje (PDF)',
                        bankTransferInstructionHint: 'Otpremite PDF sa detaljima bankovnog računa i uputstvima za plaćanje za kupce',
                        bankTransferInstructionUploadPrompt: 'Kliknite da otpremite PDF sa uputstvima za plaćanje',
                        bankTransferInstructionUploaded: 'PDF sa uputstvima za plaćanje je otpremljen',
                        setupLater: 'Podešuću ovo kasnije',
                        required: 'Obavezno',
                        monriPartnerCredentialsInfo: 'Vašim Monri akreditivima upravlja vaš servisni partner.'
                    },
                    shipping: {
                        title: 'Kategorija',
                        description: 'Izaberite kategoriju vaše prodavnice',
                        productType: 'Tip proizvoda',
                        methods: 'Načini dostave',
                        physical: 'Fizički proizvodi',
                        digital: 'Digitalni proizvodi',
                        services: 'Usluge',
                        delivery: 'Dostava na adresu',
                        pickupFromShop: 'Preuzimanje u prodavnici'
                    },
                    documentation: {
                        title: 'Uslovi korišćenja',
                        description: 'Pregledajte automatski generisane uslove korišćenja ili otpremite svoje.',
                        autoGeneratedInfo: 'Uslovi korišćenja su automatski generisani na osnovu podešavanja vaše prodavnice.',
                        termsOfService: 'Uslovi korišćenja',
                        uploadPrompt: 'Kliknite ovde ili prevucite fajl za otpremanje',
                        uploadHint: 'Samo PDF format',
                        customFileUploaded: 'Prilagođeni dokument otpremljen',
                        autoGeneratedPreview: 'Dokument će biti automatski generisan na osnovu konfiguracije vaše prodavnice',
                        tosPreviewInfo: 'Ovako će vaši uslovi korišćenja izgledati za vaše kupce.',
                        tosPreviewError: 'Nije moguće učitati pregled uslova korišćenja.',
                        uploadCustom: 'Ili otpremite vaše prilagođene uslove korišćenja'
                    },
                    summary: {
                        title: 'Sve je spremno!',
                        description: 'Pregledajte vašu konfiguraciju i završite podešavanje',
                        storeInfo: 'Informacije o prodavnici',
                        companyInfo: 'Informacije o kompaniji',
                        domainsInfo: 'Prilagođeni domeni',
                        paymentInfo: 'Načini plaćanja',
                        shippingInfo: 'Kategorija i dostava',
                        autoGenerated: 'Automatski generisano',
                        noShippingRequired: 'Fizička dostava nije potrebna'
                    }
                },
                support: {
                    stuck: 'Imate problem? Kontaktirajte podršku'
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.tos-preview-frame {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    padding: 1.5rem;
    background: #fff;
    font-size: 0.85rem;
    line-height: 1.6;
}

.onboarding-support-footer {
    text-align: center;
    padding: 1.25rem 1rem;

    .onboarding-support-link {
        font-size: 0.875rem;
        color: var(--text-muted);
        text-decoration: none;
        transition: color 0.2s ease;

        &:hover {
            color: var(--primary-start);
        }
    }
}

.public-ecommerce-onboarding {
    min-height: 100vh;

    .container-fluid {
        max-width: 960px;
        margin-left: auto;
        margin-right: auto;
    }
}

.progress-bar-container {
    margin-bottom: 2rem;
}

.progress-steps {
    display: flex;
    justify-content: space-between;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 20px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--border-default);
        z-index: 0;
    }
}

.progress-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;
    z-index: 1;

    .step-circle {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: var(--form-bg);
        border: 2px solid var(--border-default);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        color: var(--text-muted);
        margin-bottom: 0.5rem;
        transition: all 0.3s ease;
    }

    .step-label {
        font-size: 0.75rem;
        color: var(--text-muted);
        text-align: center;
        max-width: 100px;
        word-wrap: break-word;
    }

    &.active .step-circle {
        background: var(--primary-start);
        border-color: var(--primary-start);
        color: white;
    }

    &.completed .step-circle {
        background: #10b981;
        border-color: #10b981;
        color: white;
    }

    &.active .step-label,
    &.completed .step-label {
        color: var(--text-primary);
        font-weight: 500;
    }
}

.onboarding-card {
    background: var(--form-bg);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    padding: 2rem;
}

.step-icon {
    color: var(--primary-start);
}

.step-title {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.step-description {
    color: var(--text-secondary);
    font-size: 1rem;
}

.features-list {
    max-width: 500px;
    margin: 0 auto;
}

.feature-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: var(--bg-card);
    border-radius: 8px;
    margin-bottom: 0.75rem;
    color: var(--text-primary);

    svg {
        flex-shrink: 0;
    }
}

.pricing-box {
    display: inline-block;
    padding: 1.5rem 3rem;
    background: var(--bg-card);
    border: 2px solid var(--primary-start);
    border-radius: 12px;

    h5 {
        color: var(--text-secondary);
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.5rem;
    }

    .pricing-amount {
        font-size: 2rem;
        font-weight: 700;
        color: var(--primary-start);
    }
}

.summary-logo {
    max-width: 200px;
    max-height: 100px;
    object-fit: contain;
    border: 1px solid var(--border-default);
    border-radius: 8px;
    padding: 0.5rem;
    background: var(--bg-card);
}

.public-onboarding__complete-icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #28a745, #20c997);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: #fff;
    font-weight: 700;
    box-shadow: 0 8px 24px rgba(40, 167, 69, 0.35);
}

.payment-type-selection {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-top: 0.5rem;
}

.payment-type-box {
    padding: 2rem 1.5rem;
    border: 2px solid var(--border-default);
    border-radius: 12px;
    background: var(--bg-card);
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 140px;

    &:hover {
        border-color: var(--primary-start);
        background: rgba(var(--primary-start-rgb), 0.05);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &.selected {
        border-color: var(--primary-start);
        background: rgba(var(--primary-start-rgb), 0.1);
        box-shadow: 0 0 0 3px rgba(var(--primary-start-rgb), 0.1);
    }

    .b-icon {
        color: var(--primary-start);
    }

    .payment-type-name {
        color: var(--text-primary);
        font-weight: 600;
        font-size: 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
    }
}

.payment-card-logos {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.35rem;
    margin-top: 0.25rem;
}

.payment-logo-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    min-width: 52px;
    padding: 0 0.45rem;
    border-radius: 6px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.payment-logo-pill--compact {
    min-width: 46px;
}

.payment-card-logo {
    max-height: 18px;
    max-width: 44px;
    width: auto;
    height: auto;
    object-fit: contain;
}

.provider-logo {
    max-width: 130px;
    width: 100%;
    max-height: 44px;
    height: auto;
    object-fit: contain;
}

.provider-logo--wide {
    max-width: 170px;
}

// Preview/Summary styles
.preview-success-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(var(--success-rgb, 40, 167, 69), 0.1);
}

.preview-store-banner {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    background: var(--bg-secondary);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
}

.preview-store-banner-logo {
    flex-shrink: 0;
    width: 72px;
    height: 72px;
    border-radius: 10px;
    border: 1px solid var(--border-default);
    background: var(--bg-card);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.preview-banner-logo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 4px;
}

.preview-banner-logo-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.preview-store-banner-info {
    flex: 1;
    min-width: 0;
}

.preview-store-name {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
}

.preview-store-url-link {
    font-size: 0.875rem;
    color: var(--primary-start);
    text-decoration: none;
    word-break: break-all;

    &:hover {
        text-decoration: underline;
    }
}

.preview-store-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
}

.preview-cards-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.preview-card {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 12px;
    overflow: hidden;

    &--full {
        grid-column: 1 / -1;
    }
}

.preview-card-header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid var(--border-default);
    background: var(--bg-secondary);

    h6 {
        margin: 0;
        font-weight: 600;
        font-size: 0.9rem;
        color: var(--text-primary);
    }
}

.preview-card-header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: rgba(var(--primary-start-rgb), 0.1);
    flex-shrink: 0;
}

.preview-card-body {
    padding: 0.75rem 1.25rem;
}

.preview-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border-default);

    &:last-child {
        border-bottom: none;
    }
}

.preview-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-muted);
    white-space: nowrap;
    flex-shrink: 0;
    max-width: 45%;
}

.preview-value {
    font-size: 0.875rem;
    color: var(--text-primary);
    font-weight: 500;
    text-align: right;
    word-break: break-word;

    &--sm {
        font-size: 0.8rem;
    }
}

.preview-mono {
    font-family: monospace;
    font-size: 0.85rem;
    color: var(--text-secondary);
}

.preview-doc-badge {
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
}

.preview-method-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border-default);

    &:last-child {
        border-bottom: none;
    }
}

.preview-method-icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: rgba(var(--primary-start-rgb), 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
}

.preview-method-content {
    flex: 1;
    min-width: 0;
}

.preview-method-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
    display: block;
}

.document-preview {
    .dashed-box {
        border: 2px dashed var(--border-default);
        background-color: var(--form-bg);
        padding: 2rem;
        border-radius: 6px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
            border-color: var(--primary-start);
            background-color: rgba(var(--primary-start-rgb), 0.05);
        }
    }

    h5 {
        color: var(--text-primary);
        font-weight: 600;
        margin-bottom: 0.75rem;
    }
}

.navigation-buttons {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--border-default);
}

// Fix input-group alignment
::v-deep .input-group {
    align-items: stretch;
    margin-bottom: 0;

    .input-group-prepend,
    .input-group-append {
        margin-bottom: 0;

        .input-group-text {
            display: flex;
            align-items: center;
            margin-bottom: 0;
            border-width: 1px;
        }
    }

    .form-control {
        margin-bottom: 0;
        border-width: 1px;
    }
}

::v-deep .form-control,
::v-deep .custom-select,
::v-deep textarea.form-control {
    border-width: 1px;
}

::v-deep .custom-file-label {
    border-width: 1px;
}

@media (max-width: 768px) {
    .progress-steps {
        flex-wrap: wrap;
    }

    .progress-step {
        flex-basis: 50%;
        margin-bottom: 1rem;
    }

    .onboarding-card {
        padding: 1.5rem;
    }
}
</style>
